/**
 * @module support/state-object-hash-utilities
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
/* tslint:disable:max-file-line-count */
// Core Libraries
import { Injectable } from '@angular/core';
import { Profile } from './../../api/proxies/responses/api-999.interface';
import { UsageDetails } from '@support/api/proxies/responses/api-339.interface';

// Services
import { StateStoreService } from '@support/state-object-hash-utilities/services/state-store.service';
import { SETTINGS } from '@environment/settings';
import { CommonUtilService } from '@support/shared/services/common-util.service';

// Interfaces
import {
  MapObject,
  MapObjectHardgoodProperty,
} from '@support/shared/interfaces/map-object.interface';
import { DeviceSimInfo, Filter, ItemDetail, Product } from '@support/api/common/product.interface';
import { IamResponse } from '@support/api/proxies/responses/api-999.interface';
import { RepProfileResponse, RepProfile } from '@support/api/proxies/responses/api-998.interface';
import { BrowseEntry } from '@experience/shop/shared-browse/interfaces/browse-entry.interface';
import { BillingTypeEnum } from '@experience/shop/shared-browse/constants/shared-browse.constants';
import { ByodObject } from '@experience/shop/byod/interfaces/byod-landing.interface';
import {
  Cart,
  CartLine,
  CartResponse,
  MaxLinesData,
  OverrideModel,
} from '@support/api/proxies/responses/api-cart-response.interface';
import { CompleteServicesInterface } from '@experience/shop/shared-browse/interfaces/service-blade.interface';
import { CustomerSessionPassDetails } from '@support/api/proxies/responses/api-2010.interface';
import {
  CustomerUsageSummaryResults,
  CustomerUsageSummaryResultsLines,
} from '@support/api/proxies/responses/api-2009.interface';
import { CustomerLineBillingDetails } from '@support/api/proxies/responses/api-2008.interface';
import { AddedAccessories } from '@experience/shop/shared-browse/components/product-carousel/models/product-carousel.interface';
import {
  LineSetup,
  LineSetupAddresses,
} from '@experience/shop/cart/modules/line-setup/interfaces/line-setup.interface';
import {
  FamilyPlan,
  VoucherDetails,
} from '@experience/shop/softgoods/interfaces/family-plan.interface';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { RefundPostData } from '@experience/refund-request/interfaces/refund-request.interface';
import {
  OnDemandSessionPassInterface,
  SessionPassSelectedItem,
} from '@experience/change-plan-services/interface/on-demand-session-pass.interface';
import { OrderPaymentModel } from '@experience/shop/order-payment/online-order-payment/models/online-order-payment.model';
import {
  VoucherInfo,
  SubmitOrderResponseMessages,
} from '@support/api/proxies/responses/api-2150.interface';

// Customer API Interfaces
import {
  CustomerInfo,
  CustomerVerifiedOneTimePin,
} from '@support/api/proxies/responses/api-448.interface';
import { API331GetRolesPermission } from '@experience/repdash/interfaces/API-331-get-role-permissions.interface';
import { AccountInfo } from '@support/api/proxies/responses/api-451.interface';
import { EservicesParams } from '@support/api/proxies/responses/api-2235.interface';
import { EligibleServices } from '@support/api/proxies/responses/api-2102.interface';
import { ServicePostRequestInterface } from '@experience/shop/browse-services/interfaces/services.interface';
import {
  CustomerDetailsResponse,
  LineDetails,
  AddressDetails,
} from '@support/api/proxies/responses/api-2007.interface';
import {
  CustomerAccountBillingDetails,
  LineSummary,
} from '@support/api/proxies/responses/api-2006.interface';
import { CustomerRecentCharges } from '@support/api/proxies/responses/api-2018.interface';
import { CustomerListAccountsTab } from '@experience/repdash/modules/repdash-accounts/interfaces/repdash-accounts.interface';
import { GetWarrantyExchange } from '@support/api/proxies/responses/api-2911.interface';
import { StoredFilter } from '@experience/shop/hardgoods/interfaces/browse-hardgood.interface';
// Models
import { Query } from '@experience/search-results/model/query.model';
import { Tile } from '@experience/shop/product-selector/models/tile.model';
// Constant
import {
  CreditLevelEnum,
  SHARED_BROWSE_CONSTANTS,
} from '@experience/shop/shared-browse/constants/shared-browse.constants';
import {
  Terminals,
  MobilityTill,
  ConfirmationData,
  ClosePOSTillData,
  POSCashError,
  StoreData,
} from '@experience/store-operations/interfaces/store-ops.interface';
import { LinesInfo } from '@experience/repdash/interfaces/API-231-get-lines.interface';
import { OrderSearchResults } from '@support/api/proxies/responses/api-2813.interface';
import {
  LostStolenConfirmationInfo,
  ReportLostStolenInfo,
} from '@experience/report-lost-stolen/interfaces/report-lost-stolen.interface';
import { CustomerAccountLineDeviceDetails } from '@support/api/proxies/responses/api-2022.interface';
import { CustomerNotifications } from '@support/api/proxies/responses/api-2025.interface';
import { AccountLineSeviceEIPdetails } from '@support/api/proxies/responses/api-2026.interface';
import { CancelledLine } from '@experience/cancel-line/interfaces/cancel-line.interface';
import { PrepaidCart, AutoPayDiscountEligiblePlanInCart } from '@support/api/proxies/responses/prepaid-cart/prepaid-cart.interface';
import { CustomerLineDevicesCarrierStausData } from '@support/api/proxies/responses/api-2024.interface';
import { ConfirmPageInfo } from '@experience/change-msisdn/change-msisdn-confirmation/interfaces/change-msisdn-confirmation.interface';
import { CustomerAccountLineServiceDeposit } from '@support/api/proxies/responses/api-2023.interface';
import { AutoPayDetails } from '@support/api/proxies/responses/api-216.interface';
import { AutoPayConfirmationDetails } from '@support/api/proxies/responses/api-219.interface';
import { StoredPayments } from '@support/api/proxies/responses/api-144.interface';
import { ManageAutopayModel } from '@experience/manage-autopay/models/manage-autopay.model';
import { RefillCustomerData } from '@experience/refill-account/refill/interfaces/refill.interface';
import { OneTimePaymentModel } from '@experience/one-time-payment/model/one-time-payment.model';
import {
  ProductsIdAssessmentItemMap,
  RefundSelectedItems,
  SelectedItems,
} from '@experience/order-summary/interfaces/order-summary-new.interface';
import { PlanOption } from '@support/api/proxies/responses/api-2117.interface';
import { PlanDetailsResponseModel } from '@experience/shop/plan-details/models/plan-details-response.model';
import { SpecialInstructions } from '@support/api/proxies/responses/api-2032.interface';
import {
  CartLineItem,
  CartNonLineItem,
  ReplacementItem,
  ShipTo,
  InventoryCheckMap,
  InventorySellableCheckMap,
} from '@experience/shop/cart/interfaces/cart-review.interface';
import {
  LineDeivceDetails,
  lineTypeEnum,
} from '@experience/shop/product-selector/interfaces/product-selector.interface';
import { SelectedPayment } from '@support/api/proxies/requests/api-2206.interface';
import { ShippingLandingModel } from '@experience/shop/cart/modules/shipping/models/shipping-landing.model';
import { Workflow } from '@root/shared/interfaces/workflow.interface';
import { CustomerAccountLineBillingDetails } from '@support/api/proxies/responses/api-2040.interface';
import { OrderSummaryInterface } from '@support/api/proxies/responses/api-2814-new.interface';
import { JwkKey } from '@support/api/proxies/responses/api-2612.interface';
import { TapestrySearchParameterInterface } from '@experience/repdash/interfaces/lookup-emitter.interface';
import { ProductDetailsDefaultModel } from '@experience/shop/shared-browse/models/productDetails-default.model';
import { WarrantyExchangeSummaryDetails } from '@experience/device-assessment/interfaces/device-assessment.interface';
import { SpecialInstructionsType } from '@support/api/proxies/responses/api-2047.interface';
import { EpinCount } from '@experience/shop/product-details/interfaces/product-details.interface';
import { Address, AccountUserInfo } from '@support/api/proxies/responses/api-2004.interface';
import {
  HardgoodDataInterface,
  HardgoodDataProperty,
} from '@support/shared/interfaces/hardgood-data.interface';
import { CustomerEmailDetails } from '@support/api/proxies/responses/api-2005.interface';
import { CardPlanOption } from '@experience/shop/shared-browse/interfaces/plan-card-data.interface';
// import { PortInData } from '@experience/landing/rap/components/rap-interface';
import { ObjectInterface } from '@support/api/common/api-response.interface';
import { EpayResponse } from '@experience/shop/shared-browse/interfaces/entry-modal.interface';
import { NumberManagementOperations } from '@experience/change-msisdn/new-number/interface/new-number.interface';
import { CreditEligibilityResponse } from '@support/api/proxies/api-2088.interface';
import { HeaderPrimaryNav } from '@root/shared/models/header-primary-nav.interface';
// import { LineAndDeviceTile } from '@experience/landing/retail/interfaces/retail-activation.interfaces';
import { UsageBucketModel } from '@experience/landing/common/models/usage-bucket.model';
import { ImeiCheckInterface } from '@support/api/proxies/responses/api-2170.interface';
import { SimValidation } from '@support/api/proxies/responses/api-108.interface';
import {
  PdlSubtypeCartMapping,
  PdlCartPaymentMappingObject,
} from '@support/shared/interfaces/pdl-data.interface';
import { ValidateSimResponse } from '@support/api/proxies/responses/api-2900.interface';
import { ConfigurableCartAEMData } from '@core/aem/interfaces/shop/cart.interface';
import { AppConfig } from '@root/app.config';

@Injectable()
export class StateStoreUtilsService {
  public headers: Headers;
  constructor(
    private stateStoreService: StateStoreService,
    private objectService: ObjectService,
    private commonUtils: CommonUtilService,
    private appConfig: AppConfig
  ) {
    this.headers = new Headers();
  }

  /**
   * Retrieves App state
   */
  // At root App state can take any form and we cannot define a proper interface for this
  /* tslint:disable:no-any */
  public getAppState(): any {
    return this.stateStoreService.getState('app.permissionObject');
  }

  /* tslint:enable:no-any */

  /**
   * Returns previous global search
   */
  public getPreviousGlobalSearches(): string[] {
    return this.stateStoreService.getState('app.globalSearch.searchTerm') || [];
  }

  /**
   * Function stores the pervious search initiations
   * @param searches previous searches
   */
  public setPreviousGlobalSearches(searches: string[]): void {
    this.stateStoreService.setState('app.globalSearch.searchTerm', searches);
  }

  /**
   * Returns query model for global search
   */
  public getGloabalQueryModel(): Query {
    return this.stateStoreService.getState('app.globalSearch.queryModel');
  }

  /**
   * Sets query model for global search
   * @param queryModel QueryModel
   */
  public setGloabalQueryModel(queryModel: Query): void {
    this.stateStoreService.setState('app.globalSearch.queryModel', queryModel);
  }

  /**
   * Fetches the customer's Id
   * @returns string
   */

  public getCustomerId(): string {
    // if (SETTINGS.CHANNEL.toLocaleLowerCase() === 'web') {
    const webCustomerProfile: Profile = this.stateStoreService.getState('app.profile');
    return webCustomerProfile ? webCustomerProfile.id : '';
    // } else {
    //   return this.stateStoreService.getState('rep.activeCustomerId') || '';
    // }
  }

  /**
   * Fetches the Authorized customer's Id
   * @returns string
   */

  public getAuthCustomerId(): string {
    // if (SETTINGS.CHANNEL.toLocaleLowerCase() === 'web') {
    const webCustomerProfile: Profile = this.stateStoreService.getState('app.profile');
    return webCustomerProfile && webCustomerProfile.id !== 'ANON' ? webCustomerProfile.id : null;
    // } else {
    //   const assistedCustomerId = this.stateStoreService.getState('rep.activeCustomerId');
    //   return assistedCustomerId && assistedCustomerId !== 'ANON' ? assistedCustomerId : null;
    // }
  }

  /**
   * Function to set the RepActive Customer's Id
   * @param customerId
   * @returns void
   */
  public setRepActiveCustomerId(customerId: string): void {
    this.stateStoreService.setState('rep.activeCustomerId', customerId);
  }

  /**
   * Function to get the RepActive Customer's Id
   * @param customerId
   * @returns void
   */
  public getRepActiveCustomerId(): string {
    return this.stateStoreService.getState('rep.activeCustomerId');
  }

  /**
   * Function to set the rep Order Id
   * @param orderId
   * @returns void
   */
  public setCustomerOrderId(orderId: string): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.orderId`, orderId);
  }

  /**
   * Function to get rep order id
   * @returns string
   */
  public getCustomerOrderId(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.orderId`);
  }

  /**
   * Function to set dealerCode
   * @param dealerCode
   * @returns void
   */
  public setDealerCode(dealerCode: string): void {
    this.stateStoreService.setState('rep.dealerCode', dealerCode);
  }

  /**
   * Function to get dealerCode
   * @returns dealerCode
   */
  public getDealerCode(): string {
    return this.stateStoreService.getState('rep.dealerCode') || '';
  }

  /**
   * Function to set repNtId
   * @param repNtId
   * @returns void
   */
  public setRepNtId(repNtId: string): void {
    this.stateStoreService.setState('rep.ntId', repNtId);
  }

  /**
   * Function to get repNtId
   * @returns repNtId
   */
  public getRepNtId(): string {
    return this.stateStoreService.getState('rep.ntId');
  }

  /**
   * Function retrieves CustomerInfo for current customer in context
   * @param customerId customerId of current customer in context
   * @returns CustomerInfo API 448 response for stored customer information
   */
  public getCustomerInfo(customerId: string): CustomerInfo {
    return this.stateStoreService.getState(`customer.${customerId}.customerInfo`);
  }

  /**
   * Function retrieves CustomerInfo for current customer in context
   * @param customerId customerId of current customer in context
   * @return void
   */
  public setCustomerInfo(customerId: string, data: CustomerInfo): void {
    this.stateStoreService.setState(
      `customer.${customerId}.customerInfo`,
      data,
      SETTINGS.UI_CACHE.CUSTOMER_TTL
    );
  }

  /**
   * Function retrieves Usage details for current customer in context
   * @return CustomerUsageSummaryResults API 2009 response
   */
  public getUsageDetails(phoneNumber: string): CustomerUsageSummaryResults {
    const msisdn = this.get10DigitsMsisdn(phoneNumber);
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.msisdn.${msisdn}.usageDetails`
    );
  }

  /**
   * Function retrieves Usage details for current customer in context
   * @param data Usage details of current customer in context
   * @return void
   */
  public setUsageDetails(data: CustomerUsageSummaryResultsLines, phoneNumber: string): void {
    const msisdn = this.get10DigitsMsisdn(phoneNumber);
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.msisdn.${msisdn}.usageDetails`,
      data
    );
  }

  /**
   * Function retrieves line level billing details for current customer in context
   * @return CustomerLineBillingDetails API 2008 response
   */
  public getLineBillingDetails(): CustomerLineBillingDetails {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.lineBillingDetails`);
  }

  /**
   * Function sets bill block data for account in focus
   * @param data Billing Block details
   * @return void
   */
  public setBillingBlockDetails(data: CustomerAccountBillingDetails): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.billBlockInfo`, data);
  }

  /**
   * Function retrieves segmentation Code for current customer in context
   * @return SegmenationCode API 493 response
   */
  public getSegmentationCode(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.segmentationCode`);
  }

  /**
   * Function sets segmentation code
   * @param data SegmenationCode
   * @return void
   */
  public setSegmentationCode(segmentationCode: string): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.segmentationCode`,
      segmentationCode
    );
  }

  /**
   * Function set Billing Details for current customer in context
   * @param data
   * @return void
   */
  public setAccountUserInfo(data: AccountUserInfo): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.accountUserInfo`, data);
  }

  /**
   * Function retrieves Billing Details for current customer in context
   * @return void
   */
  public getAccountUserInfo(): AccountUserInfo {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.accountUserInfo`);
  }

  /**
   * Function set the Same as Account Address variable
   * @param showSameAsAccountAddress boolean
   */
  public setSameAsAccountAddress(showSameAsAccountAddress: boolean): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.sameAsAccountAddress`,
      showSameAsAccountAddress
    );
  }

  /**
   * Function get the Same as Account Address variable
   */
  public getSameAsAccountAddress(): boolean {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.sameAsAccountAddress`);
  }

  /**
   * Function retrieves bill block data for account in focus
   * @return Customer Billing Block API 2006 response
   */
  public getBillingBlockDetails(): CustomerAccountBillingDetails {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.billBlockInfo`);
  }

  /**
   * Function clears bill block data for account in focus
   * @return empty object
   */
  public clearBillingBlockDetails(): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.billBlockInfo`, null);
  }

  /**
   * Function retrieves line level billing details for current customer in context
   * @param lineId
   * @param data
   * @return void
   */
  public setLineBillingDetails(data: CustomerLineBillingDetails): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.lineBillingDetails`, data);
  }

  /**
   * Function set Billing Details for current customer in context
   * @param data
   * @return void
   */
  public setBillingAddress(data: Address): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.billingAddresses`, data);
  }

  /**
   * Function retrieves Billing Details for current customer in context
   * @return void
   */
  public getBillingAddress(): Address {
    return (
      this.stateStoreService.getState(this.getCustomerContextKey() + `.billingAddresses`) || {}
    );
  }

  /**
   * Function retrieves Browse details for current customer in context
   * @return void
   */
  public setBrowseAddress(data: Address): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.browseAddresses`, data);
  }

  /**
   * Function set Browse details for current customer in context
   * @param data
   * @return void
   */
  public getBrowseAddress(): Address {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.browseAddresses`) || {};
  }

  /**
   * Function set Billing Details for current customer in context
   * @param data
   * @return void
   */
  public setShippingAddress(data: Address): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.shippingAddresses`, data);
  }

  /**
   * Function retrieves Billing Details for current customer in context
   * @return void
   */
  public getShippingAddress(): Address {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.shippingAddresses`);
  }

  /**
   * Function retrieves session pass  details for current customer in context
   * @param lineId
   * @return CustomerSessionPassDetails
   */
  public getSessionPassDetails(phoneNumber: string): CustomerSessionPassDetails {
    const msisdn = this.get10DigitsMsisdn(phoneNumber);
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.msisdn.${msisdn}.sessionPassDetails`
    );
  }

  /**
   * Function retrieves session pass  details for current customer in context
   * @param data session pass details of current customer in context
   * @param lineId
   * @return void
   */
  public setSessionPassDetails(data: CustomerSessionPassDetails, phoneNumber: string): void {
    const msisdn = this.get10DigitsMsisdn(phoneNumber);
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.msisdn.${msisdn}.sessionPassDetails`,
      data
    );
  }

  /**
   * Function retrieves RepInfo for current rep in context
   * @returns CustomerInfo API 448 response for stored customer information
   */
  public getRoleData(): API331GetRolesPermission {
    return this.stateStoreService.getState(`roles`);
  }

  /**
   * Function retrieves Rep Info for current rep in context
   * @param roleData role and permission of current rep in context
   * @return void
   */
  public setRoleData(roleData: API331GetRolesPermission): void {
    this.stateStoreService.setState(`roles`, roleData);
  }

  /**
   * Function retrieves selected role
   * @returns string
   */
  public getRole(): string {
    return this.stateStoreService.getState(`role`);
  }

  /**
   * Function stores the selected role
   * @param role string
   */
  public setRole(role: string): void {
    this.stateStoreService.setState(`role`, role);
  }

  /**
   * Function set the Add Fan variable
   * @param role string
   */
  public setAddFan(enable: boolean): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.addFan`, enable);
  }

  /**
   * Function get the Add Fan variable
   * @param role string
   */
  public getAddFan(): boolean {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.addFan`);
  }

  /**
   * Fetches the Account Id for specified customer profile
   * @returns string
   */
  public getAccountId(): string {
    // if (SETTINGS.CHANNEL.toLocaleLowerCase() === 'web') {
    const webCustomerProfile: Profile = this.stateStoreService.getState('app.profile');
    return webCustomerProfile ? webCustomerProfile.accountId : '';
    // } else {
    //   return this.stateStoreService.getState('rep.activeAccountId') || '';
    // }
  }

  /**
   * Function to set the RepActive Account's Id
   * @param accountId
   * @returns void
   */
  public setRepActiveAccountId(accountId: string): void {
    this.stateStoreService.setState('rep.activeAccountId', accountId);
  }

  /**
   * Function to get the RepActive Account's Id
   * @param accountId
   * @returns void
   */
  public getRepActiveAccountId(): string {
    return this.stateStoreService.getState('rep.activeAccountId');
  }

  /**
   * Function to clear the RepActive Account's Id
   * @returns void
   */
  public clearRepActiveAccountId(): void {
    this.stateStoreService.clearState('rep.activeAccountId');
  }
  /**
   * Function to set the Email Data
   * @param accountId
   * @returns void
   */
  public getUserEmailData(): CustomerEmailDetails {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.emailData`);
  }

  /**
   * Function to set the Email Data
   * @param accountId
   * @returns void
   */
  public setUserEmailData(emailCustomerDetails: CustomerEmailDetails): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.emailData`,
      emailCustomerDetails
    );
  }

  /**
   * Fetches the Account type for specified customer profile
   * @returns string
   */
  public getAccountType(): string {
    return this.stateStoreService.getState('rep.activeAccountType') || '';
  }

  /**
   * Function to set the RepActive Account type
   * @param accountType
   * @returns void
   */
  public setRepActiveAccountType(accountType: string): void {
    this.stateStoreService.setState('rep.activeAccountType', accountType);
  }

  /**
   * Retrieve stored account information for customer/account in context
   * @param customerId customerId of current customer in context
   * @param accountId accountId of current account in context
   */
  public getAccountInfo(): AccountInfo {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.accountInfo`);
  }

  /**
   * Caches account information for customer/account in context
   * @param customerId customerId of current customer in context
   * @param accountId accountId of current account in context
   * @param data API 451 response data to be cached
   */
  public setAccountInfo(data: AccountInfo): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.accountInfo`,
      data,
      SETTINGS.UI_CACHE.CUSTOMER_TTL
    );
  }

  /**
   * Caches lines information recieved from verification API for customer/account
   * @param lines Info
   */
  public setAccountLinesInfo(customerDetailsResponse: CustomerDetailsResponse): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.customerDetailsResponse`,
      customerDetailsResponse
    );
  }
  /**
   * Get Cached Address information for customer/account in context
   * @return Address recieved from verification API
   */
  public getAccountLinesInfo(): CustomerDetailsResponse {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.customerDetailsResponse`
    );
  }
  /**
   * Function clears AccountLinesInfo
   */
  public clearAccountLinesInfo(): void {
    this.stateStoreService.clearState(this.getCustomerContextKey() + `.customerDetailsResponse`);
  }

  /**
   * used to set permission in stateStore
   * @param {Object} permission
   */
  public setPermission(permission: Object): void {
    this.stateStoreService.setState(`app.permissionObject`, permission);
  }

  /**
   * Caches Address information recieved from verification API for customer/account
   * @param Address Info
   */
  public setAddressDetails(addressDetails: AddressDetails): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.addressDetails`,
      addressDetails
    );
  }

  /**
   * Get Cached Address information for customer/account in context
   * @return lines recieved from verification API
   */
  public getAddressDetails(): AddressDetails {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.addressDetails`);
  }

  /**
   * Function to clear the authentication code
   */
  public clearAuthCode(): void {
    this.stateStoreService.clearState('app.authCode');
  }

  /**
   * Fetches the Authentication code
   * @return string
   */
  public getAuthCode(): string {
    return this.stateStoreService.getState('app.authCode');
  }

  /**
   * Function to set the authentication code
   * @param authCode
   * @returns void
   */
  public setAuthCode(authCode: string): void {
    this.stateStoreService.setState('app.authCode', authCode);
  }

  /**
   * Function to set the MAC Address
   * @param macAddress
   * @returns void
   */
  public setMacAddress(macAddress: string): void {
    this.stateStoreService.setState('app.macAddress', macAddress);
  }

  /**
   * Fetches the profile Id
   * @returns string
   */
  public getProfileId(): string {
    return this.stateStoreService.getState('app.profile.tmoId');
  }

  /**
   * Fetches the Rep Profile
   * @returns RepProfile
   */
  public getRepProfile(): RepProfile {
    // if (SETTINGS.CHANNEL.toLocaleLowerCase() !== 'web') {
    //   return this.stateStoreService.getState('app.profile');
    // }
    return null;
  }

  /**
   * Fetches the profile Id
   * @returns string
   */
  public getAppProfileId(): string {
    return this.stateStoreService.getState('app.profileId');
  }

  /**
   * Fetches the MAC Address
   * @returns string
   */
  public getMacAddress(): string {
    if (!this.isTapestry()) {
      return this.stateStoreService.getState('app.macAddress');
    } else {
      const deviceInfoSerialNum =
        this.getCookie('serialNumber') || localStorage.getItem('DeviceInfo.serialNumber');
      return deviceInfoSerialNum ? deviceInfoSerialNum.toLowerCase() : '';
    }
  }

  /**
   * Sets the profile Id
   * @param profileId
   * @returns void
   */
  public setProfileId(profileId: string): void {
    this.stateStoreService.setState('app.profile.tmoId', profileId);
  }

  /**
   * Sets the response for authentication login
   * @param loginResponse
   * @returns void
   */
  public setWebAuthResponse(loginResponse: IamResponse): void {
    this.stateStoreService.setState('app.profile', loginResponse.profile);
    this.setProfileId(loginResponse.tmobileId);
    this.stateStoreService.setState('app.token', loginResponse.token);
    this.stateStoreService.setState(
      'app.tempInteractionString',
      loginResponse.tempInteractionString
    );
  }

  public getProfileEmail(): string {
    return this.stateStoreService.getState('app.profile').email;
  }

  public setNavBarData(header: HeaderPrimaryNav): void {
    this.stateStoreService.setState('app.navBar', header);
  }

  public getNavBarData(): HeaderPrimaryNav {
    return this.stateStoreService.getState('app.navBar');
  }
  /**
   * Function to set smartphone equality credit eligibility
   */
  public setCreditEligibilty(eligibiltyResponse: CreditEligibilityResponse): void {
    this.stateStoreService.setState('app.smartphoneCreditEligibilty', eligibiltyResponse);
  }
  /**
   * Function to get smartphone equality credit eligibility
   */
  public getCreditEligibilty(): CreditEligibilityResponse {
    return this.stateStoreService.getState('app.smartphoneCreditEligibilty');
  }
  /**
   * Sets the query parameters from Eservices for Web Authorization
   * @param queryParams
   * @returns void
   */
  public setEservicesParams(queryParams: EservicesParams): void {
    this.stateStoreService.setState('app.eservicesParams', queryParams);
  }

  /**
   * Sets the state from Eservices for Web Authorization
   * @param queryParams
   * @returns void
   */
  public setEservicesState(state: string): void {
    this.stateStoreService.setState('app.eservicesState', state);
  }

  /**
   * Gets the state from Eservices for Web Authorization
   * @returns string
   */
  public getEservicesState(): string {
    return this.stateStoreService.getState('app.eservicesState');
  }

  /**
   * Sets the IAM token for calling merge profiles
   * @param iamToken
   * @returns void
   */
  public setJWTToken(iamToken: string): void {
    this.stateStoreService.setState('app.profile.iamToken', iamToken, true);
  }

  /**
   * Sets Response for assisted authentication
   * @param assistedResponse
   * @returns void
   */
  public setAssistedAuthResonse(assistedResponse: RepProfileResponse): void {
    this.stateStoreService.setState('app.profile', assistedResponse.profile);
    this.stateStoreService.setState(
      'app.tempInteractionString',
      assistedResponse.tempInteractionString
    );
    this.stateStoreService.setState('app.rebellionToken', assistedResponse.rebellion_token);
  }
  /**
   * Sets Response for assisted authentication
   * @param assistedResponse
   * @returns void
   */
  public getRebellionToken(): string {
    return this.stateStoreService.getState('app.rebellionToken');
  }
  /**
   * Gets the query parameters from Eservices for Web Authorization
   * @returns object
   */
  public getEservicesParams(): EservicesParams {
    return this.stateStoreService.getState('app.eservicesParams');
  }

  /**
   * Gets the iamToken for calling merge profiles
   * @returns string
   */
  public getJWTToken(): string {
    return this.stateStoreService.getState('app.profile.iamToken');
  }

  /**
   * Fetches the Authentication token
   * @returns string
   */
  public getAuthToken(): string {
    return this.stateStoreService.getState('app.token');
  }

  /**
   * Fetches the tempInteractionString which will be used as key for encryption
   * @returns string
   */
  public getTempInteractionString(): string {
    return this.stateStoreService.getState('app.tempInteractionString');
  }

  /**
   * Function used to set temp interaction id
   * @param interaction
   */
  public setTempInteractionString(interaction: string): void {
    this.stateStoreService.setState('app.tempInteractionString', interaction);
  }
  /**
   * Function to set store zip
   * @param {string} zipCode
   */
  public setStoreZipCode(zipCode: string): void {
    this.stateStoreService.setState('app.store.storeZipCode', zipCode);
  }
  /**
   * Funciton to get  store ZipCode
   * @return store status boolean
   */
  public getStoreZipCode(): string {
    return this.stateStoreService.getState('app.store.storeZipCode') || '';
  }

  /**
   * Sets store Id
   * @param id
   * @returns void
   */
  public setStoreId(id: string): void {
    this.stateStoreService.setState('app.store.storeId', id);
  }
  /**
   * Fetches whether Store is RIS enabled
   * @returns string
   */
  public getRISStoreState(): boolean {
    return this.stateStoreService.getState('app.store.risEnabled');
  }
  /**
   * Sets whether Store is RIS enabled
   * @returns boolean
   */
  public setRISStoreState(isRISStore: boolean): void {
    this.stateStoreService.setState('app.store.risEnabled', isRISStore);
  }
  /**
   * Method to gets whether SimSerialization of Store is ON/OFF
   * @returns boolean
   */
  public getSimSerialization(): string {
    return this.stateStoreService.getState('app.store.simSerialization');
  }
  /**
   * Method to sets SimSerialization of Store
   * @param simSerialization
   * @returns boolean
   */
  public setSimSerialization(simSerialization: string): void {
    this.stateStoreService.setState('app.store.simSerialization', simSerialization);
  }
  /**
   * Method to get whether License Plate of Store  is ON/OFF
   * @returns boolean
   */
  public getLicensePlate(): string {
    return this.stateStoreService.getState('app.store.licensePlate');
  }
  /**
   * Method to sets License Plate of Store
   * @param licensePlate
   * @returns boolean
   */
  public setLicensePlate(licensePlate: string): void {
    this.stateStoreService.setState('app.store.licensePlate', licensePlate);
  }
  /**
   * Sets session Id
   * @param id
   */
  public setSessionId(id: string): void {
    this.stateStoreService.setState('app.sessionId', id);
    if (!!this.getAuthCustomerId()) {
      const cookie = this.appConfig.getConfig('prida_tpp_session_id_set');
      if (cookie) {
        document.cookie = this.commonUtils.messageFormatter(cookie, [
          id,
          new Date(new Date().getTime() + cookie.exp * 60 * 1000).toUTCString(),
        ]);
      }
    }
  }

  /**
   * Returns session Id
   */
  public getSessionId(): string {
    return this.stateStoreService.getState('app.sessionId');
  }

  /**
   * Sets Device Fingerprinting script (DFP) loaded flag
   * @param isDFPLoaded
   */
  public setDFPflag(isDFPLoaded: boolean = false): void {
    this.stateStoreService.setState('app.store.isDFPLoaded', isDFPLoaded);
  }

  /**
   * Returns Device Fingerprinting script (DFP) loaded flag
   */
  public getDFPflag(): boolean {
    return this.stateStoreService.getState('app.store.isDFPLoaded');
  }

  /**
   * Sets interaction Id
   * @param id
   * @param override: set to false by default , set to true only when we are setting interactionId during login flow
   */

  public setInteractionId(id: string, override: boolean = false): void {
    // Checking if interactionId is already set in cookie ,if so , set store with it, instead of the newly generated one
    const interactionId = !override && this.getCookie('interactionId') ? this.getCookie('interactionId') : id;
    this.stateStoreService.setState(this.getCustomerContextKey() + `.interactionId`, interactionId);
  }

  /**
   * Sets interaction Id for login so that its retrieved from web.main.ts
   * @param id
   */
  public overrideInteractionIdForLogin(id: string): void {
    sessionStorage.setItem('interactionId', id);
  }
  /**
   * Gets interaction Id
   */
  public getInteractionId(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.interactionId`);
  }

  /**
   * Fetches the store Id
   * @returns string
   */
  public getStoreId(): string {
    return this.stateStoreService.getState('app.store.storeId');
  }

  /**
   * Sets the  POS Till Status
   * @param isPOSTillOpen
   * @returns void
   */
  public setPOSTillStatus(isPOSTillOpen: boolean): void {
    this.stateStoreService.setState('app.store.posTillStatus', isPOSTillOpen);
  }

  /**
   * Gets the POS Till Status
   * @returns boolean
   */
  public getPOSTillStatus(): boolean {
    return this.stateStoreService.getState('app.store.posTillStatus');
  }

  /**
   * Sets the POS Virtual Till Status
   * @returns boolean
   */
  public setPOSVirtualTillStatus(virtualTillStatus: boolean): void {
    this.stateStoreService.setState('app.store.posVirtualTillStatus', virtualTillStatus);
  }

  /**
   * Gets the POS Virtual Till Status
   * @returns boolean
   */
  public getPOSVirtualTillStatus(): boolean {
    return this.stateStoreService.getState('app.store.posVirtualTillStatus');
  }

  /**
   * Sets the terminal data
   * @param terminalData
   * @returns void
   */
  public setTerminalData(terminalData: Terminals): void {
    this.stateStoreService.setState('app.store.terminalData', terminalData);
  }

  /**
   * Gets the terminal data
   * @returns Terminals
   */
  public getTerminalData(): Terminals {
    return this.stateStoreService.getState('app.store.terminalData');
  }

  /**
   * Sets the mobility Till data
   * @param mobilityTillData
   * @returns void
   */
  public setMobilityTillData(mobilityTillData: MobilityTill[]): void {
    this.stateStoreService.setState('app.store.mobilityTillData', mobilityTillData);
  }

  /**
   * Gets the mobility Till data
   * @returns MobilityTill[]
   */
  public getTMobilityTillData(): MobilityTill[] {
    return this.stateStoreService.getState('app.store.mobilityTillData');
  }

  /**
   * Sets the Till Open data
   * @param isTillOpen
   * @returns void
   */
  public setisTillOpen(isTillOpen: boolean): void {
    this.stateStoreService.setState('app.store.isTillOpen', isTillOpen);
  }

  /**
   * Gets the Till open data
   * @returns boolean
   */

  public getisTillOpen(): boolean {
    return this.stateStoreService.getState('app.store.isTillOpen');
  }

  /**
   * Sets the Mobility Till Open
   * @param isMobilityTillOpen
   * @returns void
   */
  public setiIsMobilityTillOpen(isMobilityTillOpen: boolean): void {
    this.stateStoreService.setState('app.store.isMobilityTillOpen', isMobilityTillOpen);
  }

  /**
   * Gets the Mobility Till open
   * @returns boolean
   */

  public getIsMobilityTillOpen(): boolean {
    return this.stateStoreService.getState('app.store.isMobilityTillOpen');
  }

  /**
   * Sets the Mobility Till Open data
   * @param mobilityTillOpenData
   * @returns void
   */
  public setMobilityTillOpenData(mobilityTillOpenData: ConfirmationData): void {
    this.stateStoreService.setState('app.store.mobilityTillOpenData', mobilityTillOpenData);
  }

  /**
   * Gets the Mobility Till open data
   * @returns ConfirmationData
   */

  public getmMbilityTillOpenData(): ConfirmationData {
    return this.stateStoreService.getState('app.store.mobilityTillOpenData');
  }

  /**
   * Sets the Corporate funds
   * @param corporateRefunds
   * @returns void
   */
  public setCorporateRefunds(corporateRefunds: string[]): void {
    this.stateStoreService.setState('app.store.corporateRefunds', corporateRefunds);
  }

  /**
   * Gets the Corporate funds
   * @returns string[]
   */

  public getCorporateRefunds(): string[] {
    return this.stateStoreService.getState('app.store.corporateRefunds');
  }

  /**
   * Sets the Close POS Till data
   * @param closePOSTillData
   * @returns void
   */
  public setclosePOSTillData(closePOSTillData: ClosePOSTillData): void {
    this.stateStoreService.setState('app.store.closePOSTillData', closePOSTillData);
  }

  /**
   * Gets the Close POS Till Data
   * @returns ClosePOSTillData
   */

  public getclosePOSTillData(): ClosePOSTillData {
    return this.stateStoreService.getState('app.store.closePOSTillData');
  }

  /**
   * Sets the Over Short Cash
   * @param overshortCash
   * @returns void
   */
  public setOvershortCash(overshortCash: number): void {
    this.stateStoreService.setState('app.store.overshortCash', overshortCash);
  }

  /**
   * Gets the Over short cash
   * @returns number
   */

  public getOvershortCash(): number {
    return this.stateStoreService.getState('app.store.overshortCash');
  }

  /**
   * Sets the Over Short Refunds
   * @param overshortRefunds
   * @returns void
   */
  public setOvershortRefunds(overshortRefunds: number): void {
    this.stateStoreService.setState('app.store.overshortRefunds', overshortRefunds);
  }

  /**
   * Gets the Over short Refunds
   * @returns number
   */

  public getOvershortRefundsh(): number {
    return this.stateStoreService.getState('app.store.overshortRefunds');
  }

  /**
   * Sets the In Balance
   * @param inBalance
   * @returns void
   */
  public setInBalance(inBalance: boolean): void {
    this.stateStoreService.setState('app.store.inBalance', inBalance);
  }

  /**
   * Gets the In Balance
   * @returns boolean
   */

  public getInBalance(): boolean {
    return this.stateStoreService.getState('app.store.inBalance');
  }

  /**
   * Sets the cash Variance
   * @param cashVariance
   * @returns void
   */
  public setcashVariance(cashVariance: boolean): void {
    this.stateStoreService.setState('app.store.cashVariance', cashVariance);
  }

  /**
   * Gets the Cash Variance
   * @returns boolean
   */

  public getCashVariance(): boolean {
    return this.stateStoreService.getState('app.store.cashVariance');
  }

  /**
   * Sets the Refund Variance
   * @param refundVariance
   * @returns void
   */
  public setRefundVariance(refundVariance: boolean): void {
    this.stateStoreService.setState('app.store.refundVariance', refundVariance);
  }

  /**
   * Gets the refund Variance
   * @returns boolean
   */

  public getRefundVariance(): boolean {
    return this.stateStoreService.getState('app.store.refundVariance');
  }

  /**
   * Sets the out of balance count
   * @param outofBalanceCount
   * @returns void
   */
  public setOutofBalanceCount(outofBalanceCount: boolean): void {
    this.stateStoreService.setState('app.store.outofBalanceCount', outofBalanceCount);
  }

  /**
   * Gets the out of balance count
   * @returns boolean
   */

  public getOutofBalanceCount(): boolean {
    return this.stateStoreService.getState('app.store.outofBalanceCount');
  }

  /**
   * Sets the Store Open Date Time
   * @param storeOpenDateTime
   * @returns void
   */
  public setStoreOpenDateTime(storeOpenDateTime: string): void {
    this.stateStoreService.setState('app.store.storeOpenDateTime', storeOpenDateTime);
  }

  /**
   * Gets the Store Open Date Time
   * @returns string
   */

  public getStoreOpenDateTime(): string {
    return this.stateStoreService.getState('app.store.storeOpenDateTime');
  }

  /**
   * Sets the POS Cash Error Object
   * @param posCashErrorObject
   * @returns void
   */
  public setPosCashErrorObject(posCashErrorObject: POSCashError): void {
    this.stateStoreService.setState('app.store.posCashErrorObject', posCashErrorObject);
  }

  /**
   * Gets the POS cash Error Object
   * @returns POSCashError
   */

  public getPosCashErrorObject(): POSCashError {
    return this.stateStoreService.getState('app.store.posCashErrorObject');
  }

  /**
   * Sets the POS Till Open Confirmation Data
   * @param posTillOpenConfirmationData
   * @returns void
   */
  public setPosTillOpenConfirmationData(posTillOpenConfirmationData: ConfirmationData): void {
    this.stateStoreService.setState(
      'app.store.posTillOpenConfirmationData',
      posTillOpenConfirmationData
    );
  }

  /**
   * Gets the POS Till Open Confirmation Data
   * @returns ConfirmationData
   */

  public getposTillOpenConfirmationData(): ConfirmationData {
    return this.stateStoreService.getState('app.store.posTillOpenConfirmationData');
  }

  /**
   * Sets the POS Till Id
   * @param posTillId
   * @returns void
   */
  public setPosTillId(posTillId: string): void {
    this.stateStoreService.setState('app.store.posTillId', posTillId);
  }

  /**
   * Gets the POS Till Id
   * @returns string
   */

  public getposTillId(): string {
    return this.stateStoreService.getState('app.store.posTillId');
  }

  /**
   * Sets the POS Till Cash Amount
   * @param posTillCashAmount
   * @returns void
   */
  public setPOSTillCashAmount(posTillCashAmount: string): void {
    this.stateStoreService.setState('app.store.posTillCashAmount', posTillCashAmount);
  }

  /**
   * Gets the POS Till Cash Amount
   * @returns string
   */

  public getPOSTillCashAmount(): string {
    return this.stateStoreService.getState('app.store.posTillCashAmount');
  }

  /**
   * check whether tile in focus is default or not
   * @returns boolean
   */
  public isDefaultTileIdInFocus(): boolean {
    return this.getTileIdInFocus() === SHARED_BROWSE_CONSTANTS.NEW_LINE_ADDED;
  }
  // @TODO KRISHNA ACTION REMOVE FOLLOWING FUNCTIONS
  /**
   * Sets entry point
   * @returns void
   */
  public setEntryPoint(entryPoint: string, paramTileId?: string): void {
    const tileId = paramTileId || this.getTileIdInFocus();
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.tiles.${tileId}.entryPoint`,
      entryPoint
    );
  }

  /**
   * Gets entryid of customer in focus
   * @returns string
   */
  public getEntryPoint(tileId?: string): string {
    // Retrieve lineId for given parameter or current lineId in focus
    tileId = tileId || this.getTileIdInFocus();
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.cart.tiles.${tileId}.entryPoint`
    );
  }

  /**
   * get current Cart object
   * @returns void
   */
  public getCart(): Cart {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.cart.cartResponse`);
  }

  /**
   * set or update current Cart object
   * @returns void
   */
  public setCart(cartResponse: Cart): void {
    if (cartResponse) {
      this.stateStoreService.setState(
        this.getCustomerContextKey() + `.cart.cartResponse`,
        cartResponse
      );
      this.updateMaxLineDetails();
    } else {
      // @TODO: Handle error when invalid cartId
      console.error('ERROR: StateStoreUtils invalid cartResponse');
    }
  }

  /**
   * Removes context specific information related to cart
   */
  public deleteCart(): void {
    this.clearInstoreFlow();
    this.stateStoreService.clearState(this.getCustomerContextKey() + `.cart`);
    this.setCartId(null);
    if (this.getEpinRedemption()) {
      this.clearEpinRedemption();
    }
    this.clearEmailVerificationCache();
    this.clearSessionPass();
    this.clearServiceZeroDollarFlag();
    this.clearSelectedUpgradeLineId();
  }
  // @TODO KRISHNA ACTION REMOVE FOLLOWING FUNCTIONS
  /**
   * Removes context specific information related to cart and specific line
   */
  public deleteLine(tileId?: string): void {
    // Retrieve tileId for given parameter or current tileId in focus
    tileId = tileId || this.getTileIdInFocus();
    this.stateStoreService.clearState(this.getCustomerContextKey() + `.cart.tiles.${tileId}`);
  }

  /**
   * Function deletes state information for current tileInFocus
   * @param tileId TileId
   */
  public deleteTileModel(tileId?: string): void {
    const removeTileId = tileId || this.getTileIdInFocus();
    this.stateStoreService.clearState(this.getCustomerContextKey() + `.cart.tiles.${removeTileId}`);
  }
  // @TODO KRISHNA ACTION REMOVE FOLLOWING FUNCTIONS
  /**
   * Method to Update maximum line data
   * @returns void
   */
  public updateMaxLineDetails(): void {
    const cartResponse = this.getCart();
    let maxLinesData: MaxLinesData = this.getMaxLineDetails() || this.getMaxLineDetailsDefault();
    if (this.objectService.get(cartResponse, 'lines')) {
      const lastLineInCart = cartResponse.lines[cartResponse.lines.length - 1];
      maxLinesData = this.setMaxLines(lastLineInCart, maxLinesData);
      this.setMaxLinesReached(cartResponse.lines.length, maxLinesData);
    }

    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.maxLinesData`,
      maxLinesData
    );
  }
  // @TODO KRISHNA ACTION REMOVE FOLLOWING FUNCTIONS
  /**
   * Method to set Maximun line data
   * @param maxLinesData: MaxLinesData
   * @returns null
   */
  public setMaxLineDetails(maxLinesData: MaxLinesData): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.maxLinesData`,
      maxLinesData
    );
  }

  /**
   * Method to get Maximun line data
   * @returns MaxLinesData
   */
  public getMaxLineDetails(): MaxLinesData {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.cart.maxLinesData`);
  }

  /**
   * @method setMaxLinesReached
   * @param {cartResponse} {maxLineData}
   * @returns void
   * Sets boolean if max lines have been reached (overall)
   */
  public setMaxLinesReached(lineCount: number, maxLineData: MaxLinesData): void {
    const maxLinesReached = lineCount >= maxLineData.maxVoiceLines;
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.maxLinesReached`,
      maxLinesReached
    );
  }

  /**
   * @method getMaxLinesReached
   * @returns boolean
   * Gets boolean if max lines have been reached (overall)
   */
  public getMaxLinesReached(): boolean {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.cart.maxLinesReached`);
  }

  /**
   * Sets application defaults from AEM for max lines
   */
  public setMaxLineDetailsDefault(maxLinesData: MaxLinesData): void {
    this.stateStoreService.setState(`app.cart.defaultMaxLinesData`, maxLinesData);
  }

  /**
   * Retrieves application defaults from AEM for max lines
   */
  public getMaxLineDetailsDefault(): MaxLinesData {
    return this.stateStoreService.getState(`app.cart.defaultMaxLinesData`);
  }

  /**
   * Set line setup info during cart review
   * @param {LineSetup} lineSetup
   */
  public setLineSetup(lineSetup: LineSetup): void {
    const cartId = this.getCartId();
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.${cartId}.lineSetup`,
      lineSetup
    );
  }

  /**
   * Get line setup info in cart
   * @param {string} lineId
   * @returns {LineSetup}
   */
  public getLineSetup(): LineSetup {
    const cartId = this.getCartId();
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.cart.${cartId}.lineSetup`
    );
  }

  /**
   * Set line setup info during cart review
   * @param lineSetupFormValue
   */
  public setLineSetupFormValue(lineSetupFormValue: object): void {
    const cartId = this.getCartId();
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.${cartId}.lineSetupFormValue`,
      lineSetupFormValue
    );
  }

  /**
   * Get line setup info in cart
   * @param {string} lineId
   * @returns {LineSetup}
   */
  public getLineSetupFormValue(): object {
    const cartId = this.getCartId();
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.cart.${cartId}.lineSetupFormValue`
    );
  }

  /**
   * Set family Id
   * @returns string
   */
  public setFamilyId(selectedFamilyId: string): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.selectedFamilyId`,
      selectedFamilyId
    );
  }

  /**
   * Get familyId
   * @returns string
   */
  public getFamilyId(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.selectedFamilyId`);
  }

  /**
   * Sets cartID when cart is updated or when token is generated to get new cartID
   * @returns void
   */
  public setCartId(data: string): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.cartId`, data);
    this.updateMaxLineDetails();
  }

  /**
   * Gets cartId of customer in focus
   * @returns string
   */
  public getCartId(): string {
    return this.objectService.get(this.getCart(), 'cartId', 'CARTID') || 'CARTID';
  }

  /**
   * Sets cartID when cart is updated or when token is generated to get new cartID
   * @returns void
   */
  public setNewCartId(data: string): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.newCartId`, data);
  }

  /**
   * Gets cartId of customer in focus
   * @returns string
   */
  public getNewCartId(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.newCartId`);
  }

  /**
   * Stores selected device details of customer in focus
   * @returns void
   */
  public setSelectedDeviceDetails(data: ProductDetailsDefaultModel, productOfferId?: string): void {
    const tileId = this.getTileIdInFocus() || 'tempTileId';
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.tiles.${tileId}.deviceDetails.${productOfferId}`,
      data
    );
  }

  /**
   * Gets selected device details of customer in focus
   * @returns string
   */
  public getSelectedDeviceDetails(offerId?: string): ProductDetailsDefaultModel {
    const tileId = this.getTileIdInFocus() || 'tempTileId';
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.tiles.${tileId}.deviceDetails.${offerId}`
    );
  }

  /**
   * Sets product type
   * @returns string
   */
  public setProductType(productType?: string): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.productType`, productType);
  }

  /**
   * Gets product type
   * @returns string
   */
  public getProductType(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.productType`);
  }

  /**
   * Stores selected device details of customer in focus
   * @returns void
   */
  public setSelectedOfferId(data: string): void {
    const tileId = this.getTileIdInFocus() || 'tempTileId';
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.tiles.${tileId}.offerId`,
      data
    );
  }

  /**
   * Stores selected device details of customer in focus
   * @returns void
   */
  public getSelectedOfferId(): string {
    const tileId = this.getTileIdInFocus() || 'tempTileId';
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.tiles.${tileId}.offerId`
    );
  }
  // @TODO KRISHNA ACTION REMOVE FOLLOWING FUNCTIONS
  /**
   * Method to check byos
   * @returns boolean
   */
  public isBYOS(): boolean {
    const restoreModel = this.getByodRestoreModel();
    return this.objectService.get(restoreModel, 'simData.isSimNotNeeded');
  }
  // @TODO we should not persist data in BYOD. remove this after getting confirmation with PO
  /**
   * Sets lineId of customer in focus
   * @returns string
   */
  public setByodRestoreModel(data: ByodObject, paramTileId?: string): void {
    const tileId = paramTileId || this.getTileIdInFocus();
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.tiles.${tileId}.byodModel`,
      data
    );
  }

  /**
   * Gets lineId of customer in focus
   * @returns string
   */
  public getByodRestoreModel(contextTileId?: string): ByodObject {
    const tileId = contextTileId ? contextTileId : this.getTileIdInFocus();
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.tiles.${tileId}.byodModel`
    );
  }

  /**
   * Gets lineId of customer in focus
   * @returns string
   */
  public clearByodRestoreModel(): void {
    const tileId = this.getTileIdInFocus();
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.tiles.${tileId}.byodModel`,
      null
    );
  }
  // @TODO KRISHNA ACTION REMOVE FOLLOWING FUNCTIONS
  /**
   * Function sets tile id in focus
   * @param tileId Tile Id
   */
  public setTileIdInFocus(tileId: string): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.cart.tileIdInFocus`, tileId);
  }

  // @TODO KRISHNA ACTION REMOVE FOLLOWING FUNCTIONS
  /**
   * Function retrieves tileId in focus. If tileID is not set, then sends default tileID
   */
  public getTileIdInFocus(): string {
    const tileIdInFocus = this.stateStoreService.getState(
      this.getCustomerContextKey() + `.cart.tileIdInFocus`
    );
    if (tileIdInFocus) {
      return tileIdInFocus;
    } else {
      this.stateStoreService.setState(
        this.getCustomerContextKey() + `.cart.tileIdInFocus`,
        SHARED_BROWSE_CONSTANTS.NEW_LINE_ADDED
      );
      return SHARED_BROWSE_CONSTANTS.NEW_LINE_ADDED;
    }
  }

  /**
   * Stores initial service load data
   * @returns void
   */
  public setInitialServiceLoadData(data: CompleteServicesInterface): void {
    const tileId = this.getTileIdInFocus();
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.tiles.${tileId}.initialServiceData`,
      data
    );
  }

  /**
   * Gets initial service load data
   * @returns InitialServiceLoadDat
   */
  public getInitialServiceLoadData(): CompleteServicesInterface {
    const tileId = this.getTileIdInFocus();
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.cart.tiles.${tileId}.initialServiceData`
    );
  }

  /**
   * Get Validate LineItems in review Cart
   * @param {string} lineId
   * @returns {CartLineItem}
   */
  public getLineItems(lineId: string): CartLineItem {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.cart.lines.${lineId}.lineItems`
    );
  }

  /**
   * Set Validate LineItems in review cart
   * @param {string} lineId
   * @param {CartLineItem} cartLineItem
   */
  public setLineItems(lineId: string, cartLineItem: CartLineItem): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.lines.${lineId}.lineItems`,
      cartLineItem
    );
  }
  // @TODO remove getNonLineItems && setNonLineItems and replace with setLineItems and getLineItems
  /**
   * Get Validate NonLineItems in review cart
   * @param {string} offerId
   * @returns {CartLineItem}
   */
  public getNonLineItems(offerId: string): CartNonLineItem {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.cart.cartReviewForm.${offerId}`
    );
  }

  /**
   * Set Validate NonLineItems in review cart
   * @param {string} offerId
   * @param {CartNonLineItem} cartNonLineItem
   */
  public setNonLineItems(offerId: string, cartNonLineItem: CartNonLineItem): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.cartReviewForm.${offerId}`,
      cartNonLineItem
    );
  }

  public deleteCartReviewFormInfo(id: string): void {
    this.stateStoreService.clearState(this.getCustomerContextKey() + `.cart.lines.${id}`);
  }

  /**
   * Function used to copy and delete info in cart review form
   * @param existingCartItemId
   * @param newCartItemId
   */
  public replaceCartItemId(
    existingCartItemId: string,
    newCartItemId: string,
    deviceInfo: CartLineItem | CartNonLineItem,
    isLineItem: boolean
  ): void {
    if (isLineItem) {
      this.setLineItems(newCartItemId, deviceInfo);
    } else {
      this.setNonLineItems(newCartItemId, deviceInfo);
    }
    this.deleteCartReviewFormInfo(existingCartItemId);
  }

  /**
   * Get Exchange item in review Cart
   * @param {string} offerId
   * @returns {ReplacementItem}
   */
  public getExcahangeItem(offerId: string): ReplacementItem {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.cart.line.replacementItem.${offerId}`
    );
  }

  /**
   * Set exchange item in review cart
   * @param {string} offerId
   * @param {ReplacementItem} replacementItem
   */
  public setExcahangeItem(offerId: string, replacementItem: ReplacementItem): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.line.replacementItem.${offerId}`,
      replacementItem
    );
  }
  /**
   * Clear Validated NonLineItems in review cart
   * @param {string} offerId
   */
  public clearNonLineItems(offerId: string): void {
    const nonlineItemKeyName = SHARED_BROWSE_CONSTANTS.NONLINEITEM.concat(offerId);
    this.stateStoreService.clearState(
      this.getCustomerContextKey() + `.cart.nonLineItems.${nonlineItemKeyName}`
    );
  }

  /**
   * gets the select Credit level from the enum
   * @returns string
   */
  public getCreditLevel(): string {
    // TODO: Update method to return credit class ?
    const creditLevel = this.getCustomerConfigData()
      ? this.getCustomerConfigData().creditLevel
      : '';
    switch (creditLevel) {
      case CreditLevelEnum.EXCELLENT_CREDIT:
        return SHARED_BROWSE_CONSTANTS.excellentCreditAPIMap;
      case CreditLevelEnum.AVERAGE_CREDIT:
        return SHARED_BROWSE_CONSTANTS.avarageCreditAPIMap;
      case CreditLevelEnum.BUILDING_CREDIT:
        return SHARED_BROWSE_CONSTANTS.buildingCreditAPIMap;
      default:
        return '';
    }
  }

  /**
   * gets the select Credit class from the enum
   * @returns string
   */
  public getCustomerCreditClass(): string {
    const customerConfigData = this.getCustomerConfigData();
    return customerConfigData &&
      customerConfigData.creditCheckResponse &&
      customerConfigData.creditCheckResponse.creditLevel
      ? customerConfigData.creditCheckResponse.creditLevel
      : '';
  }
  // @TODO KRISHNA ACTION REMOVE FOLLOWING FUNCTIONS
  public setServices(services: EligibleServices): void {
    const tileId = this.getTileIdInFocus();
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.tiles.${tileId}.services`,
      services
    );
  }

  public getServices(tileId: string): EligibleServices {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.cart.tiles.${tileId}.services`
    );
  }

  public setAccessories(accessory: Object[]): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.cart.accessories`, accessory);
  }

  public getAccessories(): AddedAccessories[] {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.cart.accessories`);
  }
  // @TODO KRISHNA ACTION REMOVE FOLLOWING FUNCTIONS
  public getActiveTiles(): string[] {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.cart.activeTiles`);
  }

  public setActiveTiles(activeTiles: string[]): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.activeTiles`,
      activeTiles
    );
  }

  /**
   * Function sets tileModel for given tile id
   * @param tileModel Tile Model
   */
  public setTileModelForTileId(tileModel: Tile): void {
    const tileId = tileModel ? tileModel.tileId : 'tempTileId';
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.tiles.${tileId}.tileModel`,
      tileModel
    );
  }

  /**
   * Function retrieves tileModel data for tileId
   * @param tileId Tile Id
   */
  public getTileModelForTileId(tileId: string): Tile {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.tiles.${tileId}.tileModel`
    );
  }
  /**
   * Funciton saves hardgoodData model for specific offerId
   * @param offerId OfferId of product to save
   * @param hardgoodData HardGood data model
   */
  public setProductDetails(hardgoodData: Product, offerId?: string): void {
    const tileId = this.getTileIdInFocus() || 'tempTileId';
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.tiles.${tileId}.productDetails.${offerId}`,
      hardgoodData
    );
  }

  /**
   * Funciton saves hardgoodData model for specific offerId
   * @param offerId OfferId of product to save
   * @param hardgoodData HardGood data model
   */
  public getProductDetails(offerId: string): Product {
    const tileId = this.getTileIdInFocus() || 'tempTileId';
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.tiles.${tileId}.productDetails.${offerId}`
    );
  }

  /**
   * Funciton saves hardgoodData model for specific offerId
   * @param offerId OfferId of product to save
   * @param hardgoodData HardGood data model
   */
  public setPlanFamilyDetail(planFamilyData: FamilyPlan): void {
    const tileId = this.getTileIdInFocus();
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.tiles.${tileId}.planFamily`,
      planFamilyData
    );
  }

  /**
   * Funciton saves hardgoodData model for specific offerId
   * @param offerId OfferId of product to save
   * @param hardgoodData HardGood data model
   */
  public getPlanFamilyDetail(): FamilyPlan {
    const tileId = this.getTileIdInFocus();
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.cart.tiles.${tileId}.planFamily`
    );
  }

  /**
   * Stores zipcode data
   * @returns void
   */
  public setCustomerConfigData(data: BrowseEntry, customer?: string, account?: string): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.context`, data);
  }

  /**
   * Function clears currentLines
   * @param tileId Tile Id
   */
  public clearCurrentLineDetails(): void {
    this.stateStoreService.clearState(this.getCustomerContextKey() + `.line`);
  }

  /**
   * Clear the post void data
   *  @returns void
   */
  public clearPostVoid(): void {
    this.stateStoreService.clearState(this.getCustomerContextKey() + `.isPostVoid`);
    this.setShowPostVoid(false);
  }

  /**
   * Gets customer zipcode data
   * @returns BrowseEntry
   */
  public getCustomerConfigData(customer?: string, account?: string): BrowseEntry {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.context`);
  }

  public setBarCode(barCode: string): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.barCode`, barCode);
  }

  /**
   * Gets customer zipcode data
   * @returns string
   */
  public getBarCode(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.barCode`);
  }

  /**
   * Gets the customer in focus billing type or ANON.
   */
  public getCustomerBillingType(): string {
    const customerData = this.getCustomerConfigData();
    const customerInFocus = this.getBillingTypeOfCustomerInFocus();
    return customerInFocus
      ? customerInFocus
      : customerData
        ? BillingTypeEnum[customerData.billingType]
        : BillingTypeEnum[BillingTypeEnum.POSTPAID];
  }

  public getSettingsBillingType(): BillingTypeEnum {
    return BillingTypeEnum[SETTINGS.BILLINGTYPE && (<string>SETTINGS.BILLINGTYPE).toUpperCase()];
  }

  /**
   * Get url for previous page for cps flows
   * @returns string
   */
  public getPlansPDPPreviousState(): { isCPS: boolean } {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.cart.previousState`);
  }

  /**
   * Set url for previous page for cps flows
   * @returns void
   */
  public setPlansPDPPreviousState(isCPS: boolean): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.cart.previousState`, {
      isCPS: isCPS,
    });
  }

  /**
   * Gets url for current page
   * @returns string
   */
  public getCurrentUrl(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.currentUrl`);
  }
  /**
   * Set fresh data available flag
   * @param isFreshDataAvailable
   */
  public setFreshDataAvailableFlag(isFreshDataAvailable: boolean): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.clearCacheFlag`,
      isFreshDataAvailable
    );
  }

  /**
   * Get fresh data available flag
   * @returns boolean
   */
  public getFreshDataAvailableFlag(): boolean {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.clearCacheFlag`);
  }

  /**
   *
   * @param url Sets url for current page
   * @returns void
   */
  public setCurrentUrl(url: string): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.currentUrl`, url);
  }

  /**
   * clears url for previous page
   */
  public clearPreviousUrl(): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.previousUrl`, null);
  }

  /**
   * Gets url for previous page
   * @returns string
   */
  public getPreviousUrl(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.previousUrl`);
  }

  /**
   *
   * @param url Sets url for previous page
   * @returns void
   */
  public setPreviousUrl(url: string): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.previousUrl`, url);
  }

  /**
   * Gets entry point for CPS
   * @returns string
   */
  public getCPSEntryPoint(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.CPSEntryPoint`);
  }

  /**
   * @param url Sets entry point for CPS
   * @returns void
   */
  public setCPSEntryPoint(url: string): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.CPSEntryPoint`, url);
  }

  /**
   * setRepdashSComponentStatus method is used to set/check the modified repdash pages
   * @param flowName of type string, having flow name of repdash
   * @param data of type string having true/false values as string
   * @returns void
   */
  public setRepdashSComponentStatus(data: string, flowName: string): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.${flowName}.bladeOpen`, data);
  }

  /**
   * getRepdashComponentStatus to return blade open/close status
   * @param flowName of type string, having flow name of repdash
   * @param data of type string having true/false values as string
   * @returns string
   */
  public getRepdashComponentStatus(flowName: string): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.${flowName}.bladeOpen`);
  }

  /**
   * Funciton saves store status
   * @param status boolean
   */
  public setStoreStatus(status: boolean): void {
    this.stateStoreService.setState('app.store.storeStatus', status);
  }

  /**
   * Funciton to get store status
   * @return store status boolean
   */
  public getStoreStatus(): boolean {
    return this.stateStoreService.getState('app.store.storeStatus');
  }

  /**
   * Funciton saves override Items map
   * @param overrideItems Map
   */
  public setOverrideItems(overrideItems: Map<string, OverrideModel>): void {
    this.stateStoreService.setState('app.store.overrideItemsMap', overrideItems);
  }

  /**
   * Funciton to get override Items map
   * @return override Items map
   */
  public getOverrideItems(): Map<string, OverrideModel> {
    return this.stateStoreService.getState('app.store.overrideItemsMap');
  }

  /**
   * Function to store selected services
   * @param selected services
   */
  public setServiceData(services: ServicePostRequestInterface): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.flow.softgoods.services`,
      services
    );
  }

  /**
   * Function to get selected services
   * @return selected services
   */
  public getServiceData(): ServicePostRequestInterface {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.flow.softgoods.services`
    );
  }

  /**
   * Funciton to get Billing Type Of Customer In Focus
   * @return string
   */
  public getBillingTypeOfCustomerInFocus(): string {
    let billingType;
    if (SETTINGS.BILLINGTYPE) {
      billingType = SETTINGS.BILLINGTYPE.toLocaleLowerCase();
    }
    // @To-do Need to verify for deafult billing type value
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.billingType`)
      ? this.stateStoreService.getState(this.getCustomerContextKey() + `.billingType`)
      : billingType
        ? billingType
        : BillingTypeEnum[BillingTypeEnum.PREPAID];
  }

  /**
   * Funciton to set Billing Type Of Customer In Focus
   * @param billingType
   */
  public setBillingTypeOfCustomerInFocus(billingType: String): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.billingType`, billingType);
  }

  /**
   * Return lineId which is in focus
   * @return {string}
   */
  public getLineId(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.lineIdInFocus`);
  }

  /**
   * set lineId which need to be in focus
   * @param {string} lineId
   */
  public setLineId(lineId: string): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.lineIdInFocus`, lineId);
  }

  /**
   * Return lineId which is in focus
   * @return {string}
   */
  public getLineofServiceID(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.lineofserviceId`);
  }

  /**
   * set lineId which need to be in focus
   * @param {string} lineofserviceId
   */
  public setLineofServiceID(lineofserviceId: string): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.lineofserviceId`,
      lineofserviceId
    );
  }

  /**
   * Return lineId which is selected from upgrade
   * @return {string}
   */
  public getSelectedUpgradeLineId(): string {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.lineIdSelectedToUpGrade`
    );
  }

  /**
   * set lineId which is selected from upgrade
   * @param {string} lineId
   */
  public setSelectedUpgradeLineId(lineId: string): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.lineIdSelectedToUpGrade`,
      lineId
    );
  }

  /**
   * Clears lineId which had been selected for upgrade device
   * @return {string}
   */
  public clearSelectedUpgradeLineId(): void {
    this.stateStoreService.clearState(this.getCustomerContextKey() + `.lineIdSelectedToUpGrade`);
  }

  /**
   * Return msisdn which is in focus
   * @return {string}
   */
  public getMsisdn(): string {
    return this.get10DigitsMsisdn(
      this.stateStoreService.getState(this.getCustomerContextKey() + `.msisdnInFocus`)
    );
  }

  /**
   * set msisdn which need to be in focus
   * @param {string} msisdn
   */
  public setMsisdn(msisdn: string): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.msisdnInFocus`,
      this.get10DigitsMsisdn(msisdn)
    );
  }

  /**
   * Return sim which is in focus
   * @return {string}
   */
  public getSimNumber(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.simInFocus`);
  }

  /**
   * set msisdn which need to be in focus
   * @param {string} sim
   */
  public setSimNumber(sim: string): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.simInFocus`, sim);
  }
  /**
   * Return imei which is in focus
   * @return {string}
   */
  public getSelectedImei(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.imeiInFocus`);
  }

  /**
   * set imei which need to be in focus
   * @param {string} imei
   * @return void
   */
  public setSelectedImei(imei: string): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.imeiInFocus`, imei);
  }

  /**
   * Return selectedDate which is in focus
   * @return {string}
   */
  public getSelectedDate(): string {
    return this.stateStoreService.getState('app.selectedDate');
  }

  /**
   * set selectedDate which need to be in focus
   * @param {string} msisdn
   */
  public setSelectedDate(selectedDate: string): void {
    this.stateStoreService.setState('app.selectedDate', selectedDate);
  }

  /**
   * Sets value of see more link
   * @param boolean showSeeMore
   */
  public setShowSeeMore(showSeeMore: boolean): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.seeMoreLink`, showSeeMore);
  }

  /**
   * Gets value of see more link
   * @returns boolean
   */
  public getShowSeeMore(): boolean {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.seeMoreLink`);
  }

  /**
   * Return msisdns for multi line scenario
   * @return {Array}
   */
  public getLineMsisdns(): string[] {
    const customerDetail = this.getBillingBlockDetails();
    const lines = this.objectService.get(customerDetail, 'lines')
      ? this.objectService.get(customerDetail, 'lines')
      : [];
    return lines.map((line: LineSummary) => {
      return this.get10DigitsMsisdn(line.msisdn);
    });
  }

  /**
   * Return line ids for multi line scenario
   * @return {Array}
   */
  public getLineIds(): string[] {
    const customerDetail = this.getBillingBlockDetails();
    const lines = this.objectService.get(customerDetail, 'lines')
      ? this.objectService.get(customerDetail, 'lines')
      : [];
    return lines.map((line: LineSummary) => {
      return line.lineOfServiceId;
    });
  }

  /**
   * Set EipEligibilityMap for hardgoods
   * @param {Map<string, boolean>} map
   */
  public setEipEligibilityMap(map: Map<string, boolean>): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.eipEligibilityMap`,
      this.strMapToObj(map)
    );
  }

  /**
   * Return EipEligibilityMap for hardgoods
   * @return {Map<string, boolean>}
   */
  public getEipEligibilityMap(): Map<string, boolean> {
    return this.objToStrMap(
      this.stateStoreService.getState(this.getCustomerContextKey() + `.cart.eipEligibilityMap`)
    );
  }

  /**
   * Converts map to key value object
   * @param {MapObject} strMap
   */
  public strMapToObj(strMap: Map<string, boolean>): MapObject {
    const obj = {};
    strMap.forEach((value: boolean, key: string) => {
      obj[key] = value;
    });
    return obj;
  }

  /**
   * Converts key value object to map
   * @return {Map<string, boolean}
   */
  public objToStrMap(obj: MapObject): Map<string, boolean> {
    const strMap = new Map();
    for (const k of Object.keys(obj)) {
      strMap.set(k, obj[k]);
    }
    return strMap;
  }

  /**
   * Function sets Recent Billing Details
   * @param data Recent Billing Block details
   * @return void
   */
  public setRecentBillingDetails(data: CustomerRecentCharges): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.recentBillInfo`, data);
  }

  /**
   * Function to get LinesInfo
   * @return Lines
   */
  public getLinesInfo(): LinesInfo {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.linesInfo`);
  }

  /**
   * Function to get selected ChangeMsisdn
   * @return Lines
   */
  public getSelectedChangeMsisdn(): string {
    return this.get10DigitsMsisdn(
      this.stateStoreService.getState(this.getCustomerContextKey() + `.flow.changeMsisdn`)
    );
  }

  /**
   * Function to set selected ChangeMsisdn
   * @return Lines
   */
  public setSelectedChangeMsisdn(msisdn: string): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.flow.changeMsisdn`,
      this.get10DigitsMsisdn(msisdn)
    );
  }

  /**
   * Function to set LinesInfo
   * @return Lines
   */
  public setLinesInfo(data: LinesInfo): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.linesInfo`, data);
  }

  /**
   * Function retrieves Recent Billing Details in focus
   * @return Customer Billing Block API 2018 response
   */
  public getRecentBillingDetails(): CustomerRecentCharges {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.recentBillInfo`);
  }

  /**
   * Function to retrieve list of accounts
   * @return {CustomerListAccountsTab[]}
   */
  public getCustomerListAccountsTab(): CustomerListAccountsTab[] {
    return this.stateStoreService.getState(`rep.customerListAccountsTab`);
  }

  /**
   * Function to retrieve list of accounts
   * @return {CustomerListAccountsTab[]}
   */
  public setCustomerListAccountsTab(customerListAccountsTab: CustomerListAccountsTab[]): void {
    this.stateStoreService.setState(`rep.customerListAccountsTab`, customerListAccountsTab);
  }

  /**
   * Function to delete customer Data
   * @param {string} customerId
   */
  public deleteCustomerData(customerId?: string): void {
    if (customerId) {
      this.stateStoreService.clearState(`customer.${customerId}`);
    } else {
      this.stateStoreService.clearState(`customer`);
    }
  }

  /**
   * Return line ids for multi line scenario
   * @param lastLineInCart CartLine Object
   * @param maxLinesData MaxLine Object
   * @return {Array}
   */
  private setMaxLines(lastLineInCart: CartLine, maxLinesData: MaxLinesData): MaxLinesData {
    if (
      lastLineInCart &&
      lastLineInCart.lineType === 'HANDSET' &&
      lastLineInCart.allowedMaxLines !== 0
    ) {
      maxLinesData.maxVoiceLines = lastLineInCart.allowedMaxLines;
    }
    if (
      lastLineInCart &&
      lastLineInCart.lineType === 'TABLET' &&
      lastLineInCart.allowedMaxLines !== 0
    ) {
      maxLinesData.maxTabletLines = lastLineInCart.allowedMaxLines;
    }
    maxLinesData.maxLines =
      this.getCustomerBillingType() === BillingTypeEnum[BillingTypeEnum.POSTPAID]
        ? maxLinesData.maxVoiceLines + maxLinesData.maxTabletLines
        : 1;

    return maxLinesData;
  }

  /**
   * Function retrieves lost stolen reported date for the customer in focus
   * @return lost stolen reported date
   */
  public getLostStolenConfirmationInfo(): LostStolenConfirmationInfo {
    const msisdn = this.getMsisdn();
    const lineId = this.getLineId();
    return this.stateStoreService.getState(
      `msisdn.${msisdn}.lineId.${lineId}.lostStolenConfirmationInfo`
    );
  }

  /**
   * Function sets lost stolen reported date for the customer in focus
   * @param lostStolenConfirmationData  LostStolenConfirmationData
   */
  public setLostStolenConfirmationInfo(
    lostStolenConfirmationData: LostStolenConfirmationInfo
  ): void {
    const msisdn = this.getMsisdn();
    const lineId = this.getLineId();
    this.stateStoreService.setState(
      `msisdn.${msisdn}.lineId.${lineId}.lostStolenConfirmationInfo`,
      lostStolenConfirmationData,
      true
    );
  }

  /**
   * Function sets selected special instruction
   * @param data number
   * @return void
   */
  public setSpecialInstructionIndex(index: number): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.specialInstruction`, index);
  }

  /**
   * Function gets selected special instruction
   * @return number
   */
  public getSpecialInstructionIndex(): number {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.specialInstruction`);
  }

  /**
   * Fetches the Authentication code
   * @return string
   */
  public getStoreData(): StoreData {
    return this.stateStoreService.getState('rep.storeData');
  }

  /**
   * Function to set the authentication code
   * @param storeData
   * @returns void
   */
  public setStoreData(storeData: StoreData): void {
    this.stateStoreService.setState('rep.storeData', storeData);
  }

  /**
   * Function to get linedevice details data from cache
   * @param data Customer Account Line Device Details
   * @param {string} phoneNumber
   * @return void
   */
  public setCustomerAccountLineDeviceDetailsData(
    data: CustomerAccountLineDeviceDetails,
    phoneNumber?: string
  ): void {
    const msisdn = this.get10DigitsMsisdn(phoneNumber);
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.msisdn.${msisdn}.customerAccountLineDeviceDetailsInfo`,
      data
    );
  }

  /**
   * Function retrieves bill block data for account in focus
   * @param {string} phoneNumber
   * @return Customer Account LineDevice Details API 2022 response
   */
  public getCustomerAccountLineDeviceDetailsData(
    phoneNumber?: string
  ): CustomerAccountLineDeviceDetails {
    const msisdn = this.get10DigitsMsisdn(phoneNumber);
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.msisdn.${msisdn}.customerAccountLineDeviceDetailsInfo`
    );
  }

  /**
   * Function to get get account autopay status from cache
   * @param data Customer Account Autopay Status Data
   * @return void
   */
  public setCustomerAccountAutopayStatusData(data: CustomerNotifications): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.customerAccountAutopayStatusInfo`,
      data
    );
  }

  /**
   * Function retrieves get Customer Account AutopayStatus Data
   * @return Customer Account AutopayStatus Data API 2025 response
   */
  public getCustomerAccountAutopayStatusData(): CustomerNotifications {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.customerAccountAutopayStatusInfo`
    );
  }

  /**
   * Function to set EIP data from cache
   * @param data Customer Account Line devices EIP details API 2026
   * @return void
   */
  public setCustomerAccountLineDevicesEIPDetails(data: AccountLineSeviceEIPdetails): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.customer_Account_Line_devices_EIP_details`,
      data
    );
  }

  /**
   * Function retrieves to get EIP data from cache
   * @return Customer Account Line devices EIP details API 2026 response
   */
  public getCustomerAccountLineDevicesEIPDetails(): AccountLineSeviceEIPdetails {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.customer_Account_Line_devices_EIP_details`
    );
  }

  /**
   * Retrieves the Line Id based on tile in focus
   * @returns string
   */
  public getLineIdForTileIdInFocus(): string {
    const tileInFocus = this.getTileIdInFocus();
    const tileModel = this.getTileModelForTileId(tileInFocus);
    const tileInFocusLineId = this.objectService.get(tileModel, 'lineId');
    return tileInFocusLineId ? tileInFocusLineId : SHARED_BROWSE_CONSTANTS.NEW_LINE_ADDED;
  }

  /**
   * Retrieves the Tile Id based on line id
   * @param lineId string
   * @returns string
   */
  public getTileIdForLineId(lineId: string): string {
    const activeTiles = this.getActiveTiles();
    const length = this.objectService.get(activeTiles, 'length');
    for (let index = 0; index < length; index++) {
      const tileModel = this.getTileModelForTileId(activeTiles[index]);
      if (tileModel && tileModel.lineId && tileModel.lineId === lineId) {
        return tileModel.tileId;
      }
    }
    return null;
  }

  /**
   * Fetches the data of lines selected for cancellation
   * @return string
   */
  public getCancelledLinesData(): CancelledLine {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.flow.cancelLine.cancelledLines`
    );
  }

  /**
   * Function to set the data of lines selected for cancellation
   * @param data: stores the data for the line to be cancelled
   * @returns void
   */
  public setCancelledLinesData(data: CancelledLine): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.flow.cancelLine.cancelledLines`,
      data
    );
  }

  /**
   * Function to set the cartResponse
   * @param response: Stores the response from the cart
   * @returns void
   */
  public setCartResponse(response: PrepaidCart): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.flow.cancelLine.cartResponse`,
      response
    );
  }

  /**
   * Function to set the get the cartResponse
   * @returns void
   */
  public getCartResponse(): PrepaidCart {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.flow.cancelLine.cartResponse`
    );
  }

  /**
   * Function retrieves the changed number
   * @returns string
   */
  public getMsisdnConfirmationInfo(): ConfirmPageInfo {
    const msisdn = this.getMsisdn();
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.msisdn.${msisdn}`);
  }

  /**
   * Function stores the changed number
   * @param newNumber string
   */
  public setMsisdnConfirmationInfo(confirmPageInfo: ConfirmPageInfo): void {
    const msisdn = this.getMsisdn();
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.msisdn.${msisdn}`,
      confirmPageInfo
    );
  }

  /**
   * Clear the changed number
   * @returns void
   */
  public clearMsisdnConfirmationInfo(): void {
    const msisdn = this.getMsisdn();
    this.stateStoreService.clearState(this.getCustomerContextKey() + `.msisdn.${msisdn}.newNumber`);
  }

  /**
   * Function to identify if it is a tapestry
   * @returns {boolean}
   */
  public isTapestry(): boolean {
    const versionNum =
      this.getCookie('tapestryVersion') || localStorage.getItem('Tapestry.version');
    return !!versionNum;
  }

  public getInteractionIdFromCookie(): string {
    return this.getCookie('interactionId');
  }

  /**
   * Function to get the cookie value
   * @param cookieName Stores the name of the cookie
   * @returns string
   */
  private getCookie(cookieName: string): string {
    const match = document.cookie.match(new RegExp(`${cookieName}=([^;]+)`));
    return match ? match[1] : undefined;
  }

  /**
   * Return imeis for multi line scenario
   * @return {Array}
   */
  public getLineImeis(): string[] {
    const customerDetail = this.getBillingBlockDetails();
    const lines = this.objectService.get(customerDetail, 'lines')
      ? this.objectService.get(customerDetail, 'lines')
      : [];
    return lines.map((line: LineDetails) => {
      return line.imei;
    });
  }

  /**
   * Function to set the carrier status from API_2024
   * @param data Customer Line Devices CarrierStatus
   * @return void
   */
  public setCustomerLineDeviceCarrierStatus(data: CustomerLineDevicesCarrierStausData): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.CustomerLineDevicesCarrierStatusData`,
      data
    );
  }

  /**
   * Function to set the reasons for warranty replacement
   * @param reason string
   * @return void
   */
  public setWarrantyReplacementReasons(reason: string): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.WarrantyReplacementSelectedReason`,
      reason
    );
  }

  /**
   * Function to get the reasons for warranty replacement
   * @return string
   */
  public getWarrantyReplacementReasons(): string {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.WarrantyReplacementSelectedReason`
    );
  }

  /**
   * Function to get the carrier status from API_2024
   * @return Customer LineDevices Carrier Status Data
   */
  public getCustomerLineDeviceCarrierStatus(): CustomerLineDevicesCarrierStausData {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.CustomerLineDevicesCarrierStatusData`
    );
  }

  /**
   * Function to set applicationUserId
   * @param applicationUserId
   * @returns void
   */
  public setApplicationUserId(applicationUserId: string): void {
    this.stateStoreService.setState('app.applicationUserId', applicationUserId);
  }

  /**
   * Function to get applicationUserId
   * @returns applicationUserId
   */
  public getApplicationUserId(): string {
    return this.stateStoreService.getState('app.applicationUserId');
  }

  /**
   * Function to set applicationUserId
   * @param applicationUserId
   * @returns void
   */
  public setToken(token: string): void {
    this.stateStoreService.setState('app.token', token);
  }

  /**
   * Function to get applicationUserId
   * Getting token from localStore only for localhost, Please do not revert.
   * @returns applicationUserId
   */
  public getToken(): string {
    return this.stateStoreService.getState('app.token');
  }

  /**
   * Function to set correlation
   * @param correlation
   * @returns void
   */
  public setCorrelation(correlation: string, isAuth?: boolean): void {
    this.stateStoreService.setState('app.correlation', correlation);
    if (!!this.getAuthCustomerId() || isAuth) {
      const cookie = this.appConfig.getConfig('prida_tpp_interaction_id_set');
      if (cookie) {
        document.cookie = this.commonUtils.messageFormatter(cookie, [
          correlation,
          new Date(new Date().getTime() + cookie.exp * 60 * 1000).toUTCString(),
        ]);
      }
    }
  }

  /**
   * Function to get correlation
   * Getting token from localStore only for localhost, Please do not revert.
   * @returns correlation
   */
  public getCorrelation(): string {
    return this.stateStoreService.getState('app.correlation');
  }

  /**
   * Function to set service deposits details for audit drawer
   * @param data Customer Account Line devices service deposits details API 2023
   * @return void
   */
  public setCustomerAccountLineServiceDepositsDetails(
    data: CustomerAccountLineServiceDeposit
  ): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.Customer_Account_Line_ServiceDeposit`,
      data
    );
  }

  /**
   * Function to get service deposits details for audit drawer
   * @return Customer Account Line devices service deposits details API 2023 response
   */
  public getCustomerAccountLineServiceDepositsDetails(): CustomerAccountLineServiceDeposit {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.Customer_Account_Line_ServiceDeposit`
    );
  }

  /**
   * Function to set payment flow type
   * @param flowType
   * @return void
   */
  public setCartPaymentFlowType(flowType: string): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.cart_payment_flow`, flowType);
  }

  /**
   * Function to get payment flow type
   * @return string
   */
  public getCartPaymentFlowType(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.cart_payment_flow`);
  }

  /**
   * Function to set orderSummaryData
   * @param orderData
   * @returns void
   */
  public setOrderSummaryData(orderData: OrderSummaryInterface): void {
    const orderId = this.getCustomerOrderId();
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.flow.orderManagement.${orderId}.orderDetails`,
      orderData
    );
  }

  /**
   * Function to clear orderSummaryData
   * @param orderData
   * @returns void
   */
  public clearOrderSummaryData(): void {
    const orderId = this.getCustomerOrderId();
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.flow.orderManagement.${orderId}.orderDetails`,
      null
    );
  }
  /**
   * Function to get orderSummaryData
   * @returns orderSummaryData
   */
  public getOrderSummaryData(): OrderSummaryInterface {
    const orderId = this.getCustomerOrderId();
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.flow.orderManagement.${orderId}.orderDetails`
    );
  }

  public setRefundTicketData(ticketInfo: RefundPostData): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.flow.refundRequest.confirmation`,
      ticketInfo
    );
  }

  public getRefundTicketData(): RefundPostData {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.flow.refundRequest.confirmation`
    );
  }

  /**
   * Function to get AutoPayDetails
   * @returns {AutoPayDetails}
   */
  public getAutoPayDetails(): AutoPayDetails {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.autoPayDetails`);
  }

  /**
   * Function to store AutoPayDetails
   * @param {AutoPayDetails} autoPayDetails
   */
  public setAutoPayDetails(autoPayDetails: AutoPayDetails): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.autoPayDetails`,
      autoPayDetails
    );
  }

  /**
   * Function to get OneTimePaymentModel
   * @returns {OneTimePaymentModel}
   */
  public getOTPModelDetails(): OneTimePaymentModel {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.flow.otp`);
  }

  /**
   * Function to store OneTimePaymentModel
   * @param {OneTimePaymentModel} OneTimePaymentModel
   */
  public setOTPModelDetails(otpDetails: OneTimePaymentModel): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.flow.otp`, otpDetails);
  }

  /**
   * Function clears flow information for OTP
   */
  public clearOTPDetails(): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.flow.otp`, null);
    this.stateStoreService.setState(this.getCustomerContextKey() + `.autoPayDetails`, null);
  }

  /**
   * Gets unauthenticated number
   */
  public getOneTimePaymentNumber(): string {
    return this.stateStoreService.getState(`flow.otp.phoneNumber`);
  }

  /**
   * stores unauthentiated number
   */
  public setOneTimePaymentNumber(phoneNumber: string): void {
    this.stateStoreService.setState(`flow.otp.phoneNumber`, phoneNumber);
  }

  /**
   * Function clears unauth number.
   */
  public clearOneTimePaymentNumber(): void {
    this.stateStoreService.clearState(`flow.otp.phoneNumber`);
  }

  /**
   * Function to clear AutoPayDetails
   */
  public clearAutoPayDetails(): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.autoPayDetails`, null);
  }

  /**
   * Function to get AutoPayConfirmationDetails
   * @returns {AutoPayConfirmationDetails}
   */
  public getAutoPaySubmitDetails(): AutoPayConfirmationDetails {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.autoPayConfirmationDetails`
    );
  }

  /**
   * Function to clear AutoPayConfirmationDetails
   */
  public clearAutoPaySubmitDetails(): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.autoPayConfirmationDetails`,
      null
    );
  }

  /**
   * Function to store AutoPayConfirmationDetails
   * @param {AutoPayConfirmationDetails} autoPayConfirmationDetails
   */
  public setAutoPaySubmitDetails(autoPayConfirmationDetails: AutoPayConfirmationDetails): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.autoPayConfirmationDetails`,
      autoPayConfirmationDetails
    );
  }

  /* Function to get msisdn for redeem card un-auth flow
   * @returns string msisdn
   */
  public getRedeemCardMsisdn(): string {
    return this.get10DigitsMsisdn(this.stateStoreService.getState('app.redeemCardMsisdn'));
  }

  /**
   * Function to store msisdn for redeem card un-auth flow
   * @param string msisdn
   */
  public setRedeemCardMsisdn(msisdn: string): void {
    this.stateStoreService.setState('app.redeemCardMsisdn', this.get10DigitsMsisdn(msisdn));
  }

  /**
   * Function to clear msisdn for redeem card un-auth flow
   */
  public clearRedeemCardMsisdn(): void {
    this.stateStoreService.clearState('app.redeemCardMsisdn');
  }
  /**
   * Function to set customer Voucher Details
   * @param RefillCustomerData
   * @returns void
   */
  public setRefillCustomerData(refillCustomerData: RefillCustomerData): void {
    const msisdn = this.getMsisdn() || this.getRedeemCardMsisdn();
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.flow.redeem.${msisdn}`,
      refillCustomerData
    );
  }

  /**
   * Function to get customer Voucher Details
   * @returns RefillCustomerData
   */
  public getRefillCustomerData(): RefillCustomerData {
    const msisdn = this.getMsisdn() || this.getRedeemCardMsisdn();
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.flow.redeem.${msisdn}`);
  }

  /**
   * Function to set customer Voucher Details
   * @param RefillCustomerData
   * @returns void
   */
  public clearRefillCustomerData(): void {
    const msisdn = this.getMsisdn() || this.getRedeemCardMsisdn();
    this.stateStoreService.clearState(this.getCustomerContextKey() + `.flow.redeem.${msisdn}`);
  }
  /**
   * gets the auto pay cancellation state
   * @return string
   */
  public getAutoPayCancellationStatus(): boolean {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.autoPayCancellationStatus`
    );
  }

  /**
   * sets the auto pay cancellation state
   * @param autoPayCancelled
   * @returns void
   */
  public setAutoPayCancellationStatus(autoPayCancelled: boolean): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.autoPayCancellationStatus`,
      autoPayCancelled
    );
  }

  /**
   * Function to get StoredPaymentMethods
   * @returns {StoredPayments}
   */
  public getStoredPaymentMethods(): StoredPayments {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.storedPayment`);
  }

  /**
   * Function to store StoredPaymentMethods
   * @param {StoredPayments} storedPayment
   */
  public setStoredPaymentMethods(storedPayment: StoredPayments): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.storedPayment`, storedPayment);
  }
  /**
   * Function to store setAutopayModelDetails
   * @param {StoredPayments} storedPayment
   */
  public setAutopayModelDetails(autoPay: ManageAutopayModel): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.autoPayModelDetails`, autoPay);
  }
  /**
   * Function to store getAutopayModelDetails
   * @param {StoredPayments} storedPayment
   */
  public getAutopayModelDetails(): ManageAutopayModel {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.autoPayModelDetails`);
  }
  /**
   * Function to clear the auto pay model details
   * @returns void
   */
  public clearAutopayModelDetails(): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.autoPayModelDetails`, null);
  }
  /**
   * Function to set the Customer  emailId
   * @param emailId
   * @returns void
   */
  public setCustomerEmail(emailId: string): void {
    this.stateStoreService.setState('rep.emailId', emailId);
  }

  /**
   * Function to get the Customer  emailId
   * @param emailId
   * @returns void
   */
  public getCustomerEmail(): string {
    return this.stateStoreService.getState(`rep.emailId`);
  }

  /**
   * Function to stores Bscs customer ID
   * @param {string} bscsCustomerID
   */
  public setBscsCustomerID(bscsCustomerID: string): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.bscsCustomerID`,
      bscsCustomerID
    );
  }
  /**
   * Function to get the Customer  BSCS ID
   * @returns string
   */
  public getBscsCustomerID(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.bscsCustomerID`) || '';
  }

  /**
   * Function to set the Plan  Id
   * @param planId of type string
   * @returns void
   */
  public setPlansPDPSelectedPlanId(planId: string): void {
    const lineId = this.getLineId() || 'ANON';
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.line.${lineId}.flow.CPS.selectedPlan`,
      planId
    );
  }

  /**
   * Function to set the Plan  Id
   * @param planId of type string
   * @returns void
   */
  public clearPlansPDPSelectedPlanId(): void {
    const lineId = this.getLineId() || 'ANON';
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.line.${lineId}.flow.CPS.selectedPlan`,
      null
    );
  }

  /**
   * Function to get the Plan  Id
   * @param planId
   * @returns string
   */
  public getPlansPDPSelectedPlanId(): string {
    const lineId = this.getLineId() || 'ANON';
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.line.${lineId}.flow.CPS.selectedPlan`
    );
  }

  /**
   * Function to set Plan FamilyDetails
   * @param familyDetailModel of type PlanDetailsResponseModel
   * @returns void
   */
  public setPlanFamilyDetails(familyDetailModel: PlanDetailsResponseModel): void {
    const lineId = this.getLineId() || 'ANON';
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.line.${lineId}.flow.selectedPlanFamily`,
      familyDetailModel
    );
  }

  /**
   * Function to get Plan FamilyDetails
   * @param familyDetailModel of type PlanDetailsResponseModel
   * @returns PlanDetailsResponseModel
   */
  public getPlanFamilyDetails(): PlanDetailsResponseModel {
    const lineId = this.getLineId() || 'ANON';
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.line.${lineId}.flow.selectedPlanFamily`
    );
  }
  /**
   * Function to set selected Plan
   * @param selectedPlan of type PlanOption
   * @returns void
   */
  public setSelectedPlan(selectedPlan: CardPlanOption): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.selectedPlan`, selectedPlan);
  }

  /**
   * Function to get selected Plan
   * @param selectedPlan of type PlanOption
   * @returns PlanOption
   */
  public getSelectedPlan(): CardPlanOption {
    const customerId = this.getCustomerId() || 'ANON';
    const accountId = this.getAccountId() || 'ANON';
    return this.stateStoreService.getState(
      `customer.${customerId}.accounts.${accountId}.selectedPlan`
    );
  }
  /**
   * Function to set selected Plan
   * @param selectedPlan of type PlanOption
   * @returns void
   */
  // @TODO Function can be removed after segregation has been completed
  public setShowHideRetailPlan(selectedPlan: string): void {
    const customerId = this.getCustomerId() || 'ANON';
    const accountId = this.getAccountId() || 'ANON';
    this.stateStoreService.setState(
      `customer.${customerId}.accounts.${accountId}.selectedPlanId`,
      selectedPlan
    );
  }

  /**
   * Function to get selected Plan
   * @param selectedPlan of type PlanOption
   * @returns PlanOption
   */
  // @TODO Function can be removed after segregation has been completed
  public getShowHideRetailPlan(): string {
    const customerId = this.getCustomerId() || 'ANON';
    const accountId = this.getAccountId() || 'ANON';
    return this.stateStoreService.getState(
      `customer.${customerId}.accounts.${accountId}.selectedPlanId`
    );
  }

  /**
   * Function to get selected Plan
   * @param selectedPlan of type PlanOption
   * @returns PlanOption
   */
  // @TODO Function can be removed after segregation has been completed
  public getRetailPlanToggleKey(): string {
    const customerId = this.getCustomerId() || 'ANON';
    const accountId = this.getAccountId() || 'ANON';
    return this.stateStoreService.getState(
      `customer.${customerId}.accounts.${accountId}.retailBrowsePlanToggleKey`
    );
  }

  /**
   * Function set device assessment type
   * @param deviceAssessmentType
   * @returns void
   */
  // @TODO Function can be removed after segregation has been completed
  public setRetailPlanToggleKey(key: string): void {
    const customerId = this.getCustomerId() || 'ANON';
    const accountId = this.getAccountId() || 'ANON';
    this.stateStoreService.setState(
      `customer.${customerId}.accounts.${accountId}.retailBrowsePlanToggleKey`,
      key
    );
  }

  /**
   * Function set device assessment type
   * @param deviceAssessmentType
   * @returns void
   */
  public setDeviceAssessmentType(deviceAssessmentType: string): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.deviceAssessmentType`,
      deviceAssessmentType
    );
  }

  /**
   * Function to get device assessment type
   * @returns DeviceAssessmentType
   */
  public getDeviceAssessmentType(): string {
    return (
      this.stateStoreService.getState(this.getCustomerContextKey() + `.deviceAssessmentType`) || ''
    );
  }

  /**
   * Function to clear device assessment type
   * @returns void
   */
  public clearDeviceAssessmentType(): void {
    this.stateStoreService.clearState(this.getCustomerContextKey() + `.deviceAssessmentType`);
  }

  /**
   * Function sets warranty exchange information for the particular msisdn
   * @param data Warranty Status details
   * @return void
   */
  public setWarrantyExchangeDetails(data: GetWarrantyExchange): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.warrantStatusInfo`, data);
  }
  /**
   * Function retrieves warranty exchange information for the particular msisdn
   * @return Warranty Status API 2911 response
   */
  public getWarrantyExchangeDetails(): GetWarrantyExchange {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.warrantStatusInfo`);
  }

  /** Get selected productItem
   * @return SelectedItems
   */
  public getSelectedProductForReturn(): SelectedItems {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.selectedProductForReturn`
    );
  }

  /** Set selected productItem
   * @param items: SelectedItems
   */
  public setSelectedProductForReturn(items: SelectedItems): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.selectedProductForReturn`,
      items
    );
  }

  /** Set mannual entered SKU value
   * @param sku: string
   */
  public setManualEnteredSku(sku: string): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.flow.remorse.exchange.sku`,
      sku
    );
  }

  /** Get mannual entered SKU value
   */
  public getManualEnteredSku(): string {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.flow.remorse.exchange.sku`
    );
  }

  /** Function to clear sku value
   */
  public clearManualEnteredSku(): void {
    this.stateStoreService.clearState(this.getCustomerContextKey() + `.flow.remorse.exchange.sku`);
  }

  /** Get selected products Id Assessment Item Map
   */
  public getAssessmentProductsMap(): ProductsIdAssessmentItemMap {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.flow.remorse.productsIdAssessmentItemMap`
    );
  }

  /** Set selected products Id Assessment Item Map
   *  @param assessmentProductsMap of type ProductsIdAssessmentItemMap
   */
  public setAssessmentProductsMap(assessmentProductsMap: ProductsIdAssessmentItemMap): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.flow.remorse.productsIdAssessmentItemMap`,
      assessmentProductsMap
    );
  }

  /**
   * Function to clear products Id Assessment Item Map
   * @param {string} id
   */
  public clearAssessmentProductsMap(): void {
    this.stateStoreService.clearState(
      this.getCustomerContextKey() + `.flow.remorse.productsIdAssessmentItemMap`
    );
  }

  /** Get product details for warranty excahnge
   * @return WarrantyExchangeSummaryDetails
   */
  public getSelectedProductForWarrantyExchange(): WarrantyExchangeSummaryDetails {
    const customerId = this.getCustomerId() || 'ANON';
    const accountId = this.getAccountId() || 'ANON';
    return this.stateStoreService.getState(`customer.${customerId}.accounts.
    ${accountId}.selectedProductForWarrantyExchange`);
  }

  /** Set product details for warranty excahnge
   * @param details: WarrantyExchangeSummaryDetails
   */
  public setSelectedProductForWarrantyExchange(details: WarrantyExchangeSummaryDetails): void {
    const customerId = this.getCustomerId() || 'ANON';
    const accountId = this.getAccountId() || 'ANON';
    this.stateStoreService.setState(
      `customer.${customerId}.accounts.
    ${accountId}.selectedProductForWarrantyExchange`,
      details
    );
  }

  /**
   * Get items to be validated by Inventory check
   */
  public getInventorySellableCheckMap(): InventorySellableCheckMap {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.flow.risPurchase.inventorySellableCheckMap`
    );
  }

  /** Set items to be validated by Inventory check
   *  @param assessmentProductsMap of type ProductsIdAssessmentItemMap
   */
  public setInventorySellableCheckMap(inventoryCheckMap: InventorySellableCheckMap): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.flow.risPurchase.inventorySellableCheckMap`,
      inventoryCheckMap
    );
  }

  /**
   * Function to clear items to be validated by Inventory check
   * @param {string} id
   */
  public clearInventorySellableCheckMap(): void {
    this.stateStoreService.clearState(
      this.getCustomerContextKey() + `.flow.risPurchase.inventorySellableCheckMap`
    );
  }

  /**
   * Get items to be validated by Inventory check
   */
  public getInventoryCheckMap(): InventoryCheckMap {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.flow.risPurchase.inventoryCheckMap`
    );
  }

  /** Set items to be validated by Inventory check
   *  @param assessmentProductsMap of type ProductsIdAssessmentItemMap
   */
  public setInventoryCheckMap(inventoryCheckMap: InventoryCheckMap): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.flow.risPurchase.inventoryCheckMap`,
      inventoryCheckMap
    );
  }

  /**
   * Function to clear items to be validated by Inventory check
   * @param {string} id
   */
  public clearInventoryCheckMap(): void {
    this.stateStoreService.clearState(
      this.getCustomerContextKey() + `.flow.risPurchase.inventoryCheckMap`
    );
  }

  /**
   * Function to get appended usage details object
   */
  public getUsageDetailsData(): UsageDetails {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.usageDetailsObject`);
  }

  /** Function to get appended usage details object
   *  @param usageDetail of type UsageDetails
   */
  public setUsageDetailsData(usageDetail: UsageDetails): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.usageDetailsObject`,
      usageDetail
    );
  }

  /**
   * Function to clear usage details object
   * @param {string} id
   */
  public clearUsageDetailsData(): void {
    this.stateStoreService.clearState(this.getCustomerContextKey() + `.usageDetailsObject`);
  }

  /** Get selected color
   * @return string value
   */
  public getSelectedColor(): string {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.flow.pdp.setSelectedColor`
    );
  }

  /** Set color item
   * @param colors: string
   */
  public setSelectedColor(colors: string): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.flow.pdp.setSelectedColor`,
      colors
    );
  }

  /**
   *
   * @returns DeviceSimInfo object of current selected device
   */
  public getSelectedSimInfo(): DeviceSimInfo {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.flow.pdp.setSelectedSimInfo`
    );
  }

  /**
   * Set selected DeviceSimInfo for device
   * @param simInfo passes the selected simInfo for device
   */
  public setSelectedSimInfo(simInfo: DeviceSimInfo): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.flow.pdp.setSelectedSimInfo`,
      simInfo
    );
  }
  /** Get selected Device Family Name
   * @return string value
   */
  public getSelectedDeviceFamilyName(): string {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.flow.pdp.setSelectedDeviceFamilyName`
    );
  }

  /** Set selected Device Family Name
   * @param name: string
   */
  public setSelectedDeviceFamilyName(name: string): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.flow.pdp.setSelectedDeviceFamilyName`,
      name
    );
  }
  /** Set selected productItem
   * @param items: SelectedItems
   */
  public setActivePlanForLine(items: string): void {
    const lineId = this.getLineId() || 'ANON';
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.line.${lineId}.flow.CPS.activePlan`,
      items
    );
  }
  /** Set selected productItem
   * @param items: SelectedItems
   */
  public getActivePlanForLine(): string {
    const lineId = this.getLineId() || 'ANON';
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.line.${lineId}.flow.CPS.activePlan`
    );
  }
  /** Set newly selected plan
   * @param id: Selected Plan id
   * @returns void
   */
  public setNewPlanForLine(plan: PlanOption): void {
    const lineId = this.getLineId() || 'ANON';
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.line.${lineId}.flow.CPS.newPlan`,
      plan
    );
  }
  /** get newly selected plan
   * @returns string
   */
  public getNewPlanForLine(): PlanOption {
    const lineId = this.getLineId() || 'ANON';
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.line.${lineId}.flow.CPS.newPlan`
    );
  }
  /**
   * Clears out the new plan from cache
   * @returns string
   */
  public clearNewPlanForLine(): void {
    const lineId = this.getLineId() || 'ANON';
    this.stateStoreService.clearState(
      this.getCustomerContextKey() + `.line.${lineId}.flow.CPS.newPlan`
    );
  }

  /**
   * Function to set Item id that is selected to return
   * @param id Item Id of the item that is selected to return
   * @return void
   */
  public setItemIdForReturn(id: string): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.itemIdForReturn`, id);
  }

  /**
   *  Function to get lItem id that is selected to return
   *  @return string
   */
  public getItemIdForReturn(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.itemIdForReturn`);
  }

  /**
   * Function to set account and profile level special instructions from API_2032
   * @return void
   */
  public setSpecialInstructions(specialInstructions: SpecialInstructions): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.specialInstructions`,
      specialInstructions
    );
  }
  /**
   * Function to get account and profile level special instructions from API_2032
   * @return string
   */
  public getSpecialInstructions(): SpecialInstructions {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.specialInstructions`);
  }
  /**
   * Function to get account and profile level special instructions from API_2032
   * @return string
   */
  public deleteSpecialInstructions(): void {
    const customerId = this.getCustomerId() || 'ANON';
    const accountId = this.getAccountId() || 'ANON';
    this.stateStoreService.clearState(
      `customer.${customerId}.accounts.${accountId}.specialInstructions`
    );
  }
  /** Get selected Session pass item
   * @returns SessionPassSelectedItem
   */
  public getSessionPassSelectedItem(): SessionPassSelectedItem {
    const lineId = this.getLineId() || 'ANON';
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.line.${lineId}.flow.CPS.sessionPassSelectedItem`
    );
  }
  /** Set newly selected Session Pass Item
   * @param selectedItem: Selected Session Pass Item
   * @returns void
   */
  public setSessionPassSelectedItem(selectedItem: SessionPassSelectedItem): void {
    const lineId = this.getLineId() || 'ANON';
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.line.${lineId}.flow.CPS.sessionPassSelectedItem`,
      selectedItem
    );
  }

  /** Clear Session pass selected item
   * @param selectedItem : selected session pass item
   * @return void
   */
  public clearSessionPassSelectedItem(): void {
    const lineId = this.getLineId() || 'ANON';
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.line.${lineId}.flow.CPS.sessionPassSelectedItem`,
      null
    );
  }

  /**
   * Function to cache checkoutCart call
   * @param {Cart} cart
   */
  public setCheckoutResponse(cart: Cart): void {
    const cartId = this.getCartId();
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.${cartId}.checkOut`,
      cart
    );
  }

  /**
   * Function to retrieve checkout response from cache
   * @returns {Cart}
   */
  public getCheckoutResponse(): Cart {
    const cartId = this.getCartId();
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.cart.${cartId}.checkOut`
    );
  }

  public setAvailableMsisdnsToRelease(msisdns: string[]): void {
    const cartId = this.getCartId();
    const uniqueMsisdns: string[] = [];
    msisdns.map((msisdn: string) => {
      if (uniqueMsisdns.indexOf(msisdn) === -1) {
        uniqueMsisdns.push(msisdn);
      }
    });
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.${cartId}.availableMsisdnsToRelease`,
      uniqueMsisdns
    );
  }
  public getAvailableMsisdnsToRelease(): string[] {
    const cartId = this.getCartId();
    return (
      this.stateStoreService.getState(
        this.getCustomerContextKey() + `.cart.${cartId}.availableMsisdnsToRelease`
      ) || []
    );
  }

  /**
   * Function to cache the emergency address
   * @param {string} lineNumber for which address is being cached (L001 - L999)
   * @param {string} addressType (ppu or e911)
   * @param {string} addressId for the line to be cached
   */
  public setAddressIdsForLineSetUp(
    lineNumber: string,
    addressType: string,
    addressId: string
  ): void {
    const cartId = this.getCartId();
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.${cartId}.${lineNumber}.${addressType}`,
      addressId
    );
  }

  /**
   * Function to fetch the emergency address for the given lineNumber (L001 - L999)
   * @param {string} lineNumber for which the address is to be retrieved
   * @param {string} addressType for which the address is to ber retrieved (ppu or e911)
   * @returns address for the selected lineNumber and addressType
   */
  public getAddressForLineSetUp(lineNumber: string, addressType: string): string {
    const cartId = this.getCartId();
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.cart.${cartId}.${lineNumber}.${addressType}`
    );
  }

  /**
   * Function to clear the addresses which are posted to backend
   * @param {string} lineNumber for which address will be cleared
   * @param {string} addressType for which address will be cleared
   */
  public clearAddressForLineSetup(lineNumber: string, addressType: string): void {
    const cartId = this.getCartId();
    this.stateStoreService.clearState(
      this.getCustomerContextKey() + `.cart.${cartId}.${lineNumber}.${addressType}`
    );
  }

  /**
   * function to set selected family code of primary account
   */
  public setPlanFamliyCodeOfPrimaryAccount(familyCode: string): void {
    const cartId = this.getCartId();
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.${cartId}.primaryAccountFamilyCode`,
      familyCode
    );
  }

  /**
   * function to retrieve selected family code of primary account
   */
  public getPlanFamliyCodeOfPrimaryAccount(): string {
    const cartId = this.getCartId();
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.cart.${cartId}.primaryAccountFamilyCode`
    );
  }

  /**
   * Function to get permission for any element based on given route
   * @param url of current screen
   * @param abf which present in permission api
   * @return string
   */
  public getPermissionABFValue(url: string, abf: string): string {
    return this.objectService.get(
      this.stateStoreService.getState('app.permissionObject'),
      `${url}.elements.${abf}`,
      ''
    );
  }

  /**
   * Function to set online payment model
   * @param paymentDetails of type OrderPaymentModel
   * @return void
   */
  public setOnlineOrderPaymentModel(paymentDetails: OrderPaymentModel): void {
    const cartId = this.getCartId();
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.flow.onlinePayment.${cartId}`,
      paymentDetails
    );
  }

  /**
   * Function to get online payment model
   * @param cartId of type string
   * @return OrderPaymentModel
   */
  public getOnlineOrderPaymentModel(cartId: string): OrderPaymentModel {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.flow.onlinePayment.${cartId}`
    );
  }

  /**
   * Method to set Fraud status
   * @param fraudStatus boolean
   * @return void
   */
  public setFraudStatus(fraudStatus: boolean): void {
    const cartId = this.getCartId();
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.flow.orderReview.${cartId}`,
      fraudStatus
    );
  }

  /**
   * Method to get fraud status
   * @returns boolean
   */
  public getFraudStatus(): boolean {
    const cartId = this.getCartId();
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.flow.orderReview.${cartId}`
    );
  }

  /**
   * Function to set order shipping method
   * @param shipTo boolean that represent if shipping option is selected
   * @return void
   */
  public setShipFulfillment(shipTo: ShipTo): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.shipFulfillment`, shipTo);
  }
  /**
   * Function to get order shipping method
   * @return boolean
   */
  public getShipFulfillment(): ShipTo {
    const shipTo = this.stateStoreService.getState(
      this.getCustomerContextKey() + `.shipFulfillment`
    );
    return shipTo;
  }
  /**
   * Function to get order shipping method
   * @return boolean
   */
  public clearShipFulfillment(): void {
    const customerId = this.getCustomerId() || 'ANON';
    const accountId = this.getAccountId() || 'ANON';
    this.stateStoreService.clearState(
      `customer.${customerId}.accounts.${accountId}.shipFulfillment`
    );
  }
  /**
   * Function to get selected item in PDP
   * @param {ItemDetail} item
   */
  public setSelectedItemInPDP(item: ItemDetail): void {
    const sku = this.objectService.get(item, 'id', '');
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.tiles.${sku}.selectedItem`,
      item
    );
  }

  /**
   * Function to get selected item in PDP
   */
  public getSelectedItemInPDP(sku?: string): ItemDetail {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.tiles.${sku}.selectedItem`
    );
  }

  /**
   * Function used to set to true if existing plans api(2037) is called
   * @param {boolean} isApiCalled
   */
  public setExistingLineStatus(isApiCalled: { isApiCalled?: boolean; totalCount?: number }): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.existingPlansApiStatus`,
      isApiCalled
    );
  }

  /**
   * Function used to get the status of 2037 API
   * @returns {boolean}
   */
  public getExistingLineStatus(): { isApiCalled?: boolean; totalCount?: number } {
    return (
      this.stateStoreService.getState(this.getCustomerContextKey() + `.existingPlansApiStatus`) ||
      {}
    );
  }

  /**
   * Function used to get the status of 2037 API
   * @returns {boolean}
   */
  public clearExistingLines(): void {
    this.stateStoreService.clearState(this.getCustomerContextKey() + `.existingPlansApiStatus`);
  }
  /**
   * Function to store all tile ids
   * @param {string} id
   * @param {string} listType
   */
  public setTileIdList(id: string[], listType: lineTypeEnum): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.listType.${listType}.allLineTileIds`,
      id
    );
  }

  /**
   * Function to get all tileId's
   * @returns {string[]}
   */
  public getTileIdList(listType: lineTypeEnum, allTileIds: boolean = false): string[] {
    if (allTileIds) {
      return this.stateStoreService.getState(this.getCustomerContextKey() + `.cart.listType`) || [];
    }
    const tileIds = this.stateStoreService.getState(
      this.getCustomerContextKey() + `.cart.listType.${listType}.allLineTileIds`
    );
    return tileIds ? tileIds : [];
  }
  /**
   * Functions to store focused tile id
   * @param {string} tileId
   */
  public setFocusedTileId(tileId: string): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.cart.activation`, tileId);
  }

  public setNumberManagementMap(cartId: string, msisdnMap: NumberManagementOperations): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.cart.${cartId}`, msisdnMap);
  }

  public getNumberManagementMap(cartId: string): NumberManagementOperations {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.cart.${cartId}`);
  }

  public clearNumberManagementMap(cartId: string): void {
    this.stateStoreService.clearState(this.getCustomerContextKey() + `.cart.${cartId}`);
  }

  /**
   * Functions to set serviceThreshold of focused tile id
   * @param {string} tileId
   * @param {number} serviceThreshold
   */
  public setServiceThresholdValue(tileId: string, serviceThreshold: number): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.tile.serviceThreshold.${tileId}`,
      serviceThreshold
    );
  }

  /**
   * Functions to get serviceThreshold of focused tile id
   * @param {string} tileId
   */
  public getServiceThresholdValue(tileId: string): number {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.tile.serviceThreshold.${tileId}`
    );
  }
  /**
   * Functions to clear serviceThreshold of focused tile id
   */
  public clearServiceThresholdValue(): void {
    this.stateStoreService.clearState(this.getCustomerContextKey() + `.tile.serviceThreshold`);
  }
  /**
   * Function to get focused tile id
   * @returns {string}
   */
  public getFocusedTileId(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.cart.activation`);
  }
  /**
   * Function to store all tile ids
   * @param {string} id
   */
  public clearTileIdList(listType?: lineTypeEnum, deleteAllTiles: boolean = false): void {
    if (deleteAllTiles) {
      this.stateStoreService.clearState(this.getCustomerContextKey() + `.cart.listType`);
      return null;
    }
    this.stateStoreService.clearState(
      this.getCustomerContextKey() + `.cart.listType.${listType}.allLineTileIds`
    );
  }
  /**
   * Function to store details for focused tile id
   * @param {LineDeivceDetails} details
   * @param {string} tileId
   */
  public setFocusedTileLineNonLineItem(details: LineDeivceDetails, tileId?: string): void {
    const id = tileId ? tileId : this.getFocusedTileId();
    if (id) {
      this.stateStoreService.setState(
        this.getCustomerContextKey() + `.cart.tiles.${id}.lineNonLineItem.activation`,
        details
      );
    }
  }
  /**
   * Function to get details for focused tile id
   * @returns {LineDeivceDetails}
   */
  public getFocusedTileLineNonLineItem(
    tileId?: string,
    allLines: boolean = false
  ): LineDeivceDetails {
    if (allLines) {
      return this.stateStoreService.getState(this.getCustomerContextKey() + `.cart.tiles`);
    }
    const id = tileId ? tileId : this.getFocusedTileId();
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.cart.tiles.${id}.lineNonLineItem.activation`
    );
  }

  /**
   * Function to get details for focused tile id
   * @returns {LineDeivceDetails}
   */
  public clearFocusedTileIdDetails(tileId?: string): void {
    const id = tileId ? tileId : this.getFocusedTileId();
    this.stateStoreService.clearState(
      this.getCustomerContextKey() + `.cart.tiles.${id}.lineNonLineItem.activation`
    );
  }
  /**
   * Function to set uuidCode
   * @param uuid
   * @returns void
   */
  public setUuidCode(uuid: string): void {
    this.stateStoreService.setState('app.uuidCode', uuid);
  }

  /**
   * Function to get UuidCode
   * @returns uuid
   */
  public getUuidCode(): string {
    return this.stateStoreService.getState('app.uuidCode');
  }
  /**
   * Function to Check if account is multiline
   */
  public isMultiLine(): boolean {
    let lineInfo: CustomerAccountBillingDetails;
    lineInfo = this.getBillingBlockDetails() ? this.getBillingBlockDetails() : null;
    return lineInfo && this.objectService.get(lineInfo, 'lines.length') > 1;
  }
  /**
   * Function to set shipping model in shipping screen to be used in order confirmation screen
   * @param {ShippingLandingModel} shippingModel
   */
  public setShippingModel(shippingModel: ShippingLandingModel): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.shippingModel`, shippingModel);
  }

  /**
   * Function to get shipping model in shipping screen to be used in order confirmation screen
   * @returns {ShippingLandingModel}
   */
  public getShippingModel(): ShippingLandingModel {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.shippingModel`);
  }

  /**
   * Function to set disputed payment amount in transaction history flow
   * @param selectedPayment
   */
  public setSelectedPayment(selectedPayment: SelectedPayment): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.selectedDisputedPayment`,
      selectedPayment
    );
  }

  /**
   * Function for returning disputed payment amount in transaction history flow
   * @returns {SelectedPayment}
   */
  public getSelectedPayment(): SelectedPayment {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.selectedDisputedPayment`
    );
  }

  /**
   * Function to clear selectedDisputedPayment from state store
   */
  public clearSelectedPayment(): void {
    this.stateStoreService.clearState(this.getCustomerContextKey() + `.selectedDisputedPayment`);
  }

  /* Sets workflow Id in header
   * @param workFlowId  of type string
   */
  public setWorkflowId(workFlowId: string): void {
    const customerId = this.getCustomerId() || 'ANON';
    const accountId = this.getAccountId() || 'ANON';
    this.stateStoreService.setState(
      `app.workflow.customer.${customerId}.accounts.${accountId}.workFlowIdInFocus`,
      workFlowId
    );
  }

  /**
   * Gets workflow from AEM
   * Return workflow id of the current screen of type string
   */
  public getWorkflowId(): string {
    const customerId = this.getCustomerId() || 'ANON';
    const accountId = this.getAccountId() || 'ANON';
    return this.stateStoreService.getState(
      `app.workflow.customer.${customerId}.accounts.${accountId}.workFlowIdInFocus`
    );
  }

  /**
   * Sets current route url
   * @param current route Url of type string
   */
  public setCurrentRoute(currentRoute: string): void {
    this.stateStoreService.setState('app.workflow.currentRoute', currentRoute);
  }

  /**
   * Gets Current route
   * Return current route url of type string
   */
  public getCurrentRoute(): string {
    return this.stateStoreService.getState('app.workflow.currentRoute');
  }

  /**
   * Removes workflow id and sets it to default when flow is complete.
   */
  public removeWorkflowId(): void {
    this.stateStoreService.setState('app.workflow.workFlowIdInFocus', 'NoWorkflowDefined');
  }

  /**
   * Sets complete workflowInformation from AEM
   */
  public setWorkflowInformation(workflowInformation: Workflow[]): void {
    this.stateStoreService.setState('app.workflow.workflowInformation', workflowInformation);
  }

  /**
   * Gets complete workflow information
   * @return Workflow information of array of Workflows
   */
  public getWorkflowInformation(): Workflow[] {
    return this.stateStoreService.getState('app.workflow.workflowInformation');
  }
  /**
   * Function retrieves line level billing details for current customer in context
   * @param {string} lineOfService
   * @param {CustomerAccountLineBillingDetails} data
   * @return void
   */
  public setCustomerAccountLineBillingDetail(
    data: CustomerAccountLineBillingDetails,
    lineOfService: string
  ): void {
    const lineOfServiceId = lineOfService;
    this.stateStoreService.setState(
      this.getCustomerContextKey() +
        `
      .lineOfServiceId.${lineOfServiceId}.customerAccountLineBillingDetail`,
      data
    );
  }
  /**
   * Function retrieves line level billing details for current customer in context
   * @param {string} lineOfService
   * @return CustomerAccountLineBillingDetails API 2040 response
   */
  public getCustomerAccountLineBillingDetail(
    lineOfService: string
  ): CustomerAccountLineBillingDetails {
    const lineOfServiceId = lineOfService;
    return this.stateStoreService.getState(
      this.getCustomerContextKey() +
        `
      .lineOfServiceId.${lineOfServiceId}.customerAccountLineBillingDetail`
    );
  }
  /**
   * Function clears line level billing details for current customer in context
   * @param {string} lineOfService
   * @param {CustomerAccountLineBillingDetails} data
   * @return void
   */
  public clearCustomerAccountLineBillingDetail(): void {
    const lineOfServiceId = this.getLineId() || 'ANON';
    this.stateStoreService.clearState(
      this.getCustomerContextKey() +
        `
      .lineOfServiceId.${lineOfServiceId}.customerAccountLineBillingDetail`
    );
  }
  /**
   * used to set encryption key
   * @param JwkKey
   */
  public setEncryptionKey(encryptionKey: JwkKey): void {
    this.stateStoreService.setState('app.encryptionKey', encryptionKey);
  }

  /**
   * used to get encrypted key
   */
  public getEncryptionKey(): JwkKey {
    return this.stateStoreService.getState('app.encryptionKey');
  }

  /**
   * used to get order payment
   */
  public getOrderPayment(): OrderPaymentModel {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.cart.orderPayment`);
  }

  /**
   * use to set order payment
   * @param orderPayment
   */
  public setOrderPayment(orderPayment: OrderPaymentModel): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.orderPayment`,
      orderPayment
    );
  }

  /**
   * use to get next url
   * @return nextUrl
   */
  public getNextUrl(): string {
    const customerId = this.getCustomerId() || 'ANON';
    return this.stateStoreService.getState(`customer.${customerId}.nextUrl`);
  }

  /**
   * use to set next url
   * @param nextUrl
   */
  public setNextUrl(nextUrl: string): void {
    const customerId = this.getCustomerId() || 'ANON';
    this.stateStoreService.setState(`customer.${customerId}.nextUrl`, nextUrl);
  }
  /**
   * use to set tapestry search parameters
   * @param searchParameter
   */
  public setTapestrySearchParameters(searchParameter: TapestrySearchParameterInterface): void {
    this.stateStoreService.setState('app.tapestrySearchParameter', searchParameter);
  }

  /**
   * use to get tapestry search parameters
   * @param searchParameter
   */
  public getTapestrySearchParameters(): TapestrySearchParameterInterface {
    return this.stateStoreService.getState('app.tapestrySearchParameter');
  }
  /* Method to set Verification status on verify or bypass
   * @memberof StateStoreUtilsService
   */
  public setVerificationStatus(status: boolean): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.verified`, status);
  }
  /**
   * Method to get Verification or bypass Status
   * @memberof StateStoreUtilsService
   */
  public getVerificationStatus(): boolean {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.verified`);
  }
  /**
   * Function to set screenId for components which are not driven based on routes
   * @returns screenId
   */
  public setScreenId(screenId: string): void {
    this.stateStoreService.setState(`app.screenId`, screenId);
  }
  /**
   * Function to get screenId for components which are not driven based on routes
   * @returns screenId
   */
  public getScreenId(): string {
    return this.stateStoreService.getState(`app.screenId`);
  }
  /* Method to set faq section on support landing in focus
   * @param status
   * @returns void
   */
  // TODO: check with Goofy team if we need this and right key
  public setNavigationToFaq(status: boolean): void {
    const customerId = this.getCustomerId() || 'ANON';
    const accountId = this.getAccountId() || 'ANON';
    this.stateStoreService.setState(
      `customer.${customerId}.accounts.${accountId}.navigateToFaq_Status`,
      status
    );
  }
  /**
   * Method to get status if faq section is to be focussed
   * @returns boolean
   */
  // TODO: check with Goofy team if we need this and right key

  public getNavigationToFaq(): boolean {
    const customerId = this.getCustomerId() || 'ANON';
    const accountId = this.getAccountId() || 'ANON';
    return this.stateStoreService.getState(
      `customer.${customerId}.accounts.${accountId}.navigateToFaq_Status`
    );
  }
  /**
   * Function to set the value when device blade is clicked
   * @param accountId
   * @returns void
   */
  public setRouteToDevice(isDevice: boolean): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.isDeviceClicked`, isDevice);
  }

  /**
   * Function to get device blade clicked or not
   * @param accountId
   * @returns void
   */
  public getRouteToDevice(): boolean {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.isDeviceClicked`);
  }

  /**
   * Function to set EIP data from cache
   * @param data Customer Account Line devices EIP details API 2026
   * @return void
   */
  public setReportLostStolenData(data: ReportLostStolenInfo): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.lost_Stolen_Data`, data);
  }

  /**
   * Function to get account and profile level special instructions from API_2047
   * @return void
   */
  public getReportLostStolenData(): ReportLostStolenInfo {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.lost_Stolen_Data`);
  }

  /**
   * Function to set account and profile level special instructions from API_2047
   * @return void
   */
  public setInstructionType(data: SpecialInstructionsType): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.instruction_type_Data`, data);
  }

  /**
   * Function retrieves to get ... data from cache
   * @return Customer Account Line devices EIP details API 2047 response
   */
  public getInstructionType(): SpecialInstructionsType {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.instruction_type_Data`);
  }

  /**
   * Function to get previous customer context - customerID
   * @param accountId
   * @returns void
   */
  public getPreviousRepActiveCustomerID(): string {
    return this.stateStoreService.getState('rep.previousActiveCustomerId');
  }

  /**
   * Function to set the previousCustomerContext- CustomerID
   * @param accountId
   * @returns void
   */
  public setPreviousRepActiveCustomerID(previousCustomerID: string): void {
    this.stateStoreService.setState('rep.previousActiveCustomerId', previousCustomerID);
  }

  /**
   * Function to get previous customer context - customerID
   * @param accountId
   * @returns void
   */
  public getPreviousRepActiveAccountID(): string {
    return this.stateStoreService.getState('rep.previousActiveAccountId');
  }

  /**
   * Function to set the previousCustomerContext- CustomerID
   * @param accountId
   * @returns void
   */
  public setPreviousRepActiveAccountID(previousAccountID: string): void {
    this.stateStoreService.setState('rep.previousActiveAccountId', previousAccountID);
  }

  private getCustomerContextKey(): string {
    const customerId = this.getCustomerId() || 'ANON';
    const accountId = this.getAccountId() || 'ANON';
    return `customer.${customerId}.accounts.${accountId}`;
  }

  /**
   * Function to set epin voucher information
   * @param voucherInfo stores epin information
   * @return void
   */
  public setVoucherInfo(voucherInfo: VoucherInfo[]): void {
    const customerId = this.getCustomerId() || 'ANON';
    const accountId = this.getAccountId() || 'ANON';
    this.stateStoreService.setState(
      `customer.${customerId}.accounts.${accountId}.voucherInfo`,
      voucherInfo
    );
  }
  /**
   * Function to get epin voucher info
   * @return VoucherInfo[]
   */
  public getVoucherInfo(): VoucherInfo[] {
    const customerId = this.getCustomerId() || 'ANON';
    const accountId = this.getAccountId() || 'ANON';
    const voucherInfo = this.stateStoreService.getState(
      `customer.${customerId}.accounts.${accountId}.voucherInfo`
    );
    return voucherInfo;
  }

  /**
   * Function to set epin cart information
   * @param epinCount stores epin information
   * @return void
   */
  public setEpinCart(epinCount: EpinCount): void {
    const customerId = this.getCustomerId() || 'ANON';
    const accountId = this.getAccountId() || 'ANON';
    this.stateStoreService.setState(
      `customer.${customerId}.accounts.${accountId}.epinCount`,
      epinCount
    );
  }
  /**
   * Function to get epin cart info
   * @return epinCount
   */
  public getEpinCart(): EpinCount {
    const customerId = this.getCustomerId() || 'ANON';
    const accountId = this.getAccountId() || 'ANON';
    const epinCount = this.stateStoreService.getState(
      `customer.${customerId}.accounts.${accountId}.epinCount`
    );
    return epinCount;
  }
  /**
   * Function to get account and profile level special instructions from API_2032
   * @return string
   */
  public deleteEpinCart(): void {
    const customerId = this.getCustomerId() || 'ANON';
    const accountId = this.getAccountId() || 'ANON';
    this.stateStoreService.clearState(`customer.${customerId}.accounts.${accountId}.epinCount`);
  }

  /**
   * Method to set whether the line is portIn
   * @param portIn boolean
   * @param msisdn string
   * @returns void
   */
  public setPortInEligibility(portIn: boolean, msisdn: string): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.portIn.${this.get10DigitsMsisdn(msisdn)}`,
      portIn
    );
  }

  /**
   * Method to get whether the line is portIn
   * @param msisdn string
   * @returns boolean
   */
  public getPortInEligibility(msisdn: string): boolean {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.cart.portIn.${this.get10DigitsMsisdn(msisdn)}`
    );
  }
  /**
   * Method to get isPostVoid confirmation
   * @param isPostVoid
   * @returns boolean
   */
  public setShowPostVoid(showPostVoid: boolean): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.isPostVoid`, showPostVoid);
  }
  public getShowPostVoid(): boolean {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.isPostVoid`);
  }
  /**
   * Function to set the new rep order id after postvoid
   * @param orderId
   * @returns void
   */
  public setNewOrderIdforPostvoid(orderId: string): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + 'postvoid' + `.orderId`,
      orderId
    );
  }

  /**
   * Function to set the original rep order id after postvoid
   * @param orderId
   * @returns void
   */
  public setOriginalOrderIdforPostvoid(orderId: string): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + 'postvoid' + `.originalOrderId`,
      orderId
    );
  }

  /**
   * Function to get rep new postvoid order id
   * @returns string
   */
  public getOriginalOrderIdforPostvoid(): string {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + 'postvoid' + `.originalOrderId`
    );
  }

  /**
   * Function used to store entered MSISDN in refill account(E-Pin Scenario)
   * @param {string} msisdn
   */
  public setEpinMsisdn(msisdn: string): void {
    const customerId = this.getCustomerId() || 'ANON';
    const accountId = this.getAccountId() || 'ANON';
    this.stateStoreService.setState(
      `customer.${customerId}.accounts.${accountId}.anon.epinMsisdn`,
      msisdn
    );
  }

  /**
   * Function used to get store entered MSISDN in refill account(E-Pin Scenario)
   * @returns {string}
   */
  public getEpinMsisdn(): string {
    const customerId = this.getCustomerId() || 'ANON';
    const accountId = this.getAccountId() || 'ANON';
    const msisdn = this.stateStoreService.getState(
      `customer.${customerId}.accounts.${accountId}.anon.epinMsisdn`
    );
    return this.get10DigitsMsisdn(msisdn);
  }
  /**
   * Function to get rep new postvoid order id
   * @returns string
   */
  public getNewOrderIdforPostvoid(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + 'postvoid' + `.orderId`);
  }

  /* Sets the imei searched on imei block check page in header
   * @param imeiSearched of type string
   */
  public setSearchedImei(imeiSearched: string): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.searchedImei`, imeiSearched);
  }

  /**
   * Gets the imei searched on imei block check page
   * Returns searched imei
   */
  public getSearchedImei(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.searchedImei`);
  }

  /**
   * Sets the imei searched on imei block check page in header
   * @param imeiSearched of type string
   */
  public setOrderID(orderId: string): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.orderId`, orderId);
  }

  /**
   * Gets the imei searched on imei block check page
   * Returns searched imei
   */
  public getOrderID(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.orderId`);
  }

  /**
   * Function to get pending orders from API 2813
   * @returns OrderSearchResults[]
   */
  public getPendingOrders(lineId: string): OrderSearchResults[] {
    const customerId = this.getCustomerId() || 'ANON';
    const accountId = this.getAccountId() || 'ANON';
    return this.stateStoreService.getState(
      `customer.${customerId}.accounts.${accountId}.line.${lineId}.getPendingOrders_orderSearchResults`
    );
  }

  /**
   * Function to set pending orders from API 2813
   * @returns void
   */
  public setPendingOrders(orderSearchResults: OrderSearchResults[], lineId: string): void {
    const customerId = this.getCustomerId() || 'ANON';
    const accountId = this.getAccountId() || 'ANON';
    this.stateStoreService.setState(
      `customer.${customerId}.accounts.${accountId}.line.${lineId}.getPendingOrders_orderSearchResults`,
      orderSearchResults
    );
  }
  /**
   * Function to get 10 digits msisdn if msisdn length is > 10
   * @returns string
   */
  private get10DigitsMsisdn(msisdn: string): string {
    return this.commonUtils.formatMsisdn10Digits(msisdn);
  }
  /**
   * get next bill cycle date for CPS flow
   * @returns string
   */
  public getNextBillingCycle(): string {
    const lineId = this.getLineId() || 'ANON';
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.line.${lineId}.flow.CPS.setEffectiveDate`
    );
  }
  /**
   * set next bill cycle date for CPS flow
   * @returns void
   */
  public setNextBillingCycle(nextBillingDate: string): void {
    const lineId = this.getLineId() || 'ANON';
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.line.${lineId}.flow.CPS.setEffectiveDate`,
      nextBillingDate
    );
  }

  /** Get Pre Purchased Plan Voucher Details
   * @return voucher name
   */
  public getPrePurchasedPlanVoucherDetails(): VoucherDetails {
    const lineId = this.getLineId() || 'ANON';
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.cart.${lineId}.flow.prePurchasedPlan`
    );
  }

  /** Set Pre Purchased Plan Voucher Details
   * @param voucher: VoucherDetails
   */
  public setPrePurchasedPlanVoucherDetails(voucher: VoucherDetails): void {
    const lineId = this.getLineId() || 'ANON';
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.${lineId}.flow.prePurchasedPlan`,
      voucher
    );
  }

  /** Get selected products Map
   * @return SelectedItems
   */
  public getHardGoodsCachedData(): HardgoodDataInterface {
    const objOfMap: MapObjectHardgoodProperty =
      this.stateStoreService.getState('app.hardgoodsCacheMap');
    if (objOfMap) {
      return { mappedData: this.objectToHardgoodCacheMap(objOfMap) };
    }
    return null;
  }

  /** Set selected Cached products Map
   * @param items: SelectedItems
   */
  public setHardGoodsCachedData(productsCacheMap: HardgoodDataInterface): void {
    const mappedData: Map<string, HardgoodDataProperty> = productsCacheMap.mappedData;
    this.stateStoreService.setState(
      'app.hardgoodsCacheMap',
      this.convertMapToObjectForProductMap(mappedData)
    );
  }

  /**
   * Function to clear Cached products Map
   * @param {string} id
   */
  public clearHardGoodsCachedData(): void {
    this.stateStoreService.clearState('app.hardgoodsCacheMap');
  }

  /**
   * Converts map to key value object to avoid cloning issue
   * @param {MapObject} strMap
   */
  private convertMapToObjectForProductMap(
    strMap: Map<string, HardgoodDataProperty>
  ): MapObjectHardgoodProperty {
    const obj = {};
    strMap.forEach((value: HardgoodDataProperty, key: string) => {
      obj[key] = value;
    });
    return obj;
  }

  /**
   * Converts key value object to map
   * @return {Map<string, boolean}
   */
  private objectToHardgoodCacheMap(
    obj: MapObjectHardgoodProperty
  ): Map<string, HardgoodDataProperty> {
    const strMap = new Map();
    for (const k of Object.keys(obj)) {
      strMap.set(k, obj[k]);
    }
    return strMap;
  }

  /**
   * setMyProfileUrl to set flag for myprofile route
   */
  public setMyProfileUrl(url: string): void {
    this.stateStoreService.setState('customer.myprofile.url', url);
  }
  /**
   * getMyProfileUrl to get flag for myprofile route
   * @return string
   */
  public getMyProfileUrl(): string {
    return this.stateStoreService.getState('customer.myprofile.url');
  }

  /**
   * Method to set returnAll
   * @param isReturnAllChecked
   * @returns boolean
   */
  public setReturnAll(isReturnAllChecked: boolean): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.isReturnAllChecked`,
      isReturnAllChecked
    );
  }
  /**
   * Method to get returnAll
   * @returns boolean
   */
  public getReturnAll(): boolean {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.isReturnAllChecked`);
  }
  /**
   * clears the line level billing details for current customer in context
   * @param {string} lineOfServiceId
   * @return void
   */
  public clearCustomerBillingDetailsData(lineOfServiceId?: string): void {
    this.stateStoreService.clearState(
      this.getCustomerContextKey() +
        `
      .lineOfServiceId.${lineOfServiceId}.customerAccountLineBillingDetail`
    );
  }
  /**
   * Method to  set Cart checkout eligibility for Remorse Exchange cart
   * @param enableCheckout
   * @returns boolean
   */
  public setRemorseExchangeCheckout(enableCheckout: boolean): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.flow.remorseExchange.checkout`,
      enableCheckout
    );
  }

  /**
   * Method to  get Cart checkout eligibility for Remorse Exchange cart
   * @param isPostVoid
   * @returns boolean
   */

  public getRemorseExchangeCheckout(): boolean {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.flow.remorseExchange.checkout`
    );
  }

  /**
   * Clear the Cart checkout eligibility for Remorse Exchange cart
   *  @returns void
   */
  public RemorseExchangeCheckout(): void {
    this.stateStoreService.clearState(
      this.getCustomerContextKey() + `.flow.remorseExchange.checkout`
    );
  }
  /**
   * Set the session pass details response for that customer
   *  @returns void
   */
  public setOnDemandPassesDetails(sessionPassDetails: OnDemandSessionPassInterface): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.flow.CPS.passesDetails`,
      sessionPassDetails
    );
  }

  /**
   * Get the session pass details response for that customer
   *  @returns void
   */
  public getOnDemandPassesDetails(): OnDemandSessionPassInterface {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.flow.CPS.passesDetails`
    );
  }
  /**
   * Get the session pass details response for that customer
   *  @returns void
   */
  public setCompleteCartResponse(cartResponse: CartResponse): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.completeCartResponse`,
      cartResponse
    );
  }
  /**
   * Get the session pass details response for that customer
   *  @returns void
   */
  public getCompleteCartResponse(): CartResponse {
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.cart.completeCartResponse`
    );
  }
  /**
   * Clear the customer context on successful order creation
   *  @returns void
   */
  public clearCustomerInformation(): void {
    this.stateStoreService.clearState(this.getCustomerContextKey());
  }

  /**
   * Set the cache data for the customer in focus for the context screen
   *  @returns void
   */
  // tslint:disable no-any
  public setCacheDataForBackWardNavigation(context: string, cacheData: any): void {
    const cartId = this.getCartId();
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.${cartId}.${context}Cache`,
      cacheData
    );
  }

  /**
   * Get the cache data for the customer in focus for the context screen
   *  @returns cache data for the context
   */
  public getCacheDataForBackWardNavigation(context: string): any {
    const cartId = this.getCartId();
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.${cartId}.${context}Cache`
    );
  }

  /**
   * set the boolean flag if user is doing device transaction or not
   *  @params isUpgrade: boolean
   */
  public setUpgradeFlow(isUpgrade: boolean): void {
    const cartId = this.getCartId();
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.${cartId}.isUpgradeFlow`,
      isUpgrade
    );
  }
  /**
   * Get the boolean flag if user is doing device transaction or not
   *  @returns boolean
   */
  public getUpgradeFlow(): boolean {
    const cartId = this.getCartId();
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.${cartId}.isUpgradeFlow`
    );
  }

  /**
   * Set the cache data for refund item
   *  @returns void
   */
  public setRefundData(items: RefundSelectedItems[]): void {
    const orderId = this.getCustomerOrderId();
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.flow.orderManagement.${orderId}.selectedProductForRefund`,
      items
    );
  }

  /**
   * Get the cache data for refund item
   *  @returns cache data for the context
   */
  public getRefundData(): RefundSelectedItems[] {
    const orderId = this.getCustomerOrderId();
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.flow.orderManagement.${orderId}.selectedProductForRefund`
    );
  }

  /**
   * Function set the Accessory in browse accessory catagory
   * @param role string
   */
  public setAccessoryDetails(productName: string): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.accessoryDetails`,
      productName
    );
  }

  /**
   * Function clear the Accessory in browse accessory catagory
   */
  public clearAccessoryDetails(): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.accessoryDetails`, null);
  }

  /**
   * Function get the Accessry in browse accessory catagory
   * @returns role string
   */
  public getAccessoryDetails(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.accessoryDetails`);
  }

  /**
   * Function set compatible accessory offerId in browse device catagory
   * @param role string
   */
  public setOfferId(offerId: string): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.OfferId`, offerId);
  }
  /**
   * Function get compatible  offerId in browse device catagory
   * @returns role string
   */
  public getOfferId(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.OfferId`);
  }

  /**
   * Function clear the compatible accessory offerId in browse accessory catagory
   */
  public clearAccessoryOfferId(): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.accessoryOfferId`, null);
  }

  /**
   * Function get compatible accessory offerId in browse accessory catagory
   * @returns role string
   */
  public getAccessoryOfferId(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.accessoryOfferId`);
  }

  /**
   * Function set compatible accessory offerId in browse accessory catagory
   * @param role string
   */
  public setAccessoryOfferId(offerId: string): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.accessoryOfferId`, offerId);
  }
  /**
   * Clear the cached refund item
   *  @returns void
   */
  public clearRefundData(): void {
    const orderId = this.getCustomerOrderId();
    this.stateStoreService.clearState(
      this.getCustomerContextKey() + `.flow.orderManagement.${orderId}.selectedProductForRefund`
    );
  }
  /**
   * Set the Refundable Amount for refund item
   *  @returns void
   */
  public setRefundAmount(refundItemAmount: String): void {
    this.stateStoreService.setState(`refundItemAmount`, refundItemAmount);
  }

  /**
   * Get the Refundable Amount for refund item
   *  @returns Refundable Amount for refund item
   */
  public getRefundAmount(): string {
    return this.stateStoreService.getState(`refundItemAmount`);
  }

  /**
   * Method to get Active plan id for the current line
   * @returns string
   */
  public getCurrentActivePlan(): string {
    const currentLine = this.objectService.get(
      this.getCustomerAccountLineBillingDetail(this.getLineId()),
      'lineDetails'
    );
    if (currentLine) {
      return this.objectService.get(currentLine, 'plan.code');
    } else {
      return '';
    }
  }
  // tslint:enable no-any
  /*** Function set the filter in browse accessory catagory
   * @param role string
   */
  public setAccessoryFilter(filterObject: StoredFilter, filterType: string): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.${filterType}`, filterObject);
  }

  /**
   * Function get the filter in browse accessory catagory
   * @param role string
   */
  public getAccessoryFilter(filterType: string): StoredFilter {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.${filterType}`);
  }

  /**
   * get linesetup addresses to be displayed on screen
   * @returns {any}
   */
  public getLineSetupAddresses(): LineSetupAddresses {
    return (
      this.stateStoreService.getState(this.getCustomerContextKey() + '.lineSetupAddresses') || {
        newAddress: {},
      }
    );
  }

  /**
   * function to add address to linesetup addresses, adds with key as stringified address, value as address itself
   * @param {Address} address
   */
  public addAddressToLineSetupAddress(address: Address): string {
    const lineSetupAddresses = this.getLineSetupAddresses();
    let stringifiedAddress: string = 'newAddress';
    if (address.addressLine1) {
      stringifiedAddress = this.commonUtils.stringifyAddress(address);
      address.places = stringifiedAddress;
      lineSetupAddresses[stringifiedAddress] = address;
    } else {
      lineSetupAddresses[stringifiedAddress] = {};
    }

    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.lineSetupAddresses`,
      lineSetupAddresses
    );

    return stringifiedAddress;
  }
  /**
   * This Function is to set selectedChargeIndex selected by user.
   * @param {number} index
   */
  public setSelectedChargeIndex(index: number): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.selectedChargeIndex`, index);
  }

  /**
   * This Function is to get  selectedChargeIndex selected by user.
   * @return {string}
   */
  public getselectedChargeIndex(): string {
    return this.stateStoreService
      .getState(this.getCustomerContextKey() + `.selectedChargeIndex`)
      .toString();
  }
  /**
   * This Function is to get  selectedChargeIndex selected by user.
   * @return {string}
   */
  public setAccountFraudStatus(accountId: string, accountFraudStatus: boolean): void {
    this.stateStoreService.setState(`accounts.${accountId}.accountFraudStatus`, accountFraudStatus);
  }
  /**
   * This Function is to get  selectedChargeIndex selected by user.
   * @return {string}
   */
  public getAccountFraudStatus(): boolean {
    const accountId = this.getAccountId();
    return this.stateStoreService.getState(`accounts.${accountId}.accountFraudStatus`);
  }
  /**
   * This Function is to clear accoun.
   * @return {string}
   */
  public clearAccountFraudStatus(): void {
    const accountId = this.getAccountId();
    this.stateStoreService.setState(`accounts.${accountId}.accountFraudStatus`, null);
  }
  /**
   * This Function is to set  activity Ids for cache clear APIs
   * @return {string}
   */
  public setActivityIdsforCacheClearing(activityId: string): void {
    const intId = this.getInteractionId();
    let activityIdExisting = this.getActivityIdsforCacheClearing();
    if (activityIdExisting && activityIdExisting.length > 0) {
      activityIdExisting.push(activityId);
    } else {
      activityIdExisting = [activityId];
    }
    this.stateStoreService.setState(`app.${intId}.activityIdCaching`, activityIdExisting);
  }
  /**
   * This Function is to get  activity Ids for cache clear APIs
   * @return {string}
   */
  public getActivityIdsforCacheClearing(): string[] {
    const intId = this.getInteractionId();
    return this.stateStoreService.getState(`app.${intId}.activityIdCaching`);
  }

  /**
   * This Function is to clear actiivty IDs whose caches have been cleared
   */
  public clearCacheActivityIDs(): void {
    const intId = this.getInteractionId();
    this.stateStoreService.clearState(`app.${intId}.activityIdCaching`);
  }
  /**
   * Sets the scandit status of tapestry on load of application
   *  @returns void
   */
  public setScanditTapestryStatus(status: boolean): void {
    this.stateStoreService.setState(`scanditTapestryStatus`, status);
  }

  /**
   * Gets the scandit status of tapestry
   *  @returns boolean
   */
  public getScanditTapestryStatus(): boolean {
    return this.stateStoreService.getState(`scanditTapestryStatus`);
  }

  /**
   * Sets the SkipVerification status of tapestry
   *  @returns void
   */
  public setSkipVerificationStatus(status: boolean): void {
    this.stateStoreService.setState(`rep.skipVerification`, status);
  }
  /**
   * Gets the SkipVerification status of tapestry
   *  @returns boolean
   */
  public getSkipVerificationStatus(): boolean {
    return this.stateStoreService.getState(`rep.skipVerification`);
  }

  /**
   * Checks if it is a citrix machine
   *  @returns void
   */
  public isCitrix(): boolean {
    return !!sessionStorage.getItem('citrix');
  }

  /**
   * Sets the validated Sim of Rap
   *  @returns void
   */
  // @TODO Function can be removed after segregation has been completed
  public setValidatedSim(sim: string): void {
    this.stateStoreService.setState('app.rap.validatedSim', sim);
  }

  /**
   * Gets the validated Sim of Rap
   * @returns string
   */
  // @TODO Function can be removed after segregation has been completed
  public getValidatedSim(): string {
    return this.stateStoreService.getState('app.rap.validatedSim');
  }

  /**
   * Sets the validated Imei of Rap
   *  @returns void
   */
  // @TODO Function can be removed after segregation has been completed
  public setValidatedImei(imei: string): void {
    this.stateStoreService.setState('app.rap.validatedImei', imei);
  }

  /**
   * Gets the validated Imei of Rap
   * @returns string
   */
  // @TODO Function can be removed after segregation has been completed
  public getValidatedImei(): string {
    return this.stateStoreService.getState('app.rap.validatedImei');
  }

  /**
   * Gets the selected quantity
   * @returns string
   */

  public getQuantity(selectedId: string): number {
    return this.stateStoreService.getState(`app.${selectedId}.quantity`);
  }

  /**
   * Sets the quantity for a device
   *  @returns void
   */
  public setQuantity(qty: number, selectedId: string): void {
    this.stateStoreService.setState(`app.${selectedId}.quantity`, qty);
  }

  /**
   * Sets the Reserved Msisdn of Rap
   *  @returns void
   */
  // @TODO Function can be removed after segregation has been completed
  public setReservedMsisdn(msisdn: string): void {
    this.stateStoreService.setState('app.rap.reservedMsisdn', msisdn);
  }

  /**
   * Gets the Reserved Msisdn of Rap
   * @returns string
   */
  // @TODO Function can be removed after segregation has been completed
  public getReservedMsisdn(): string {
    return this.stateStoreService.getState('app.rap.reservedMsisdn');
  }

  /**
   * Sets the Port In Indicator
   *  @returns void
   */
  // @TODO Function can be removed after segregation has been completed
  public setPortInIndicator(val: boolean): void {
    this.stateStoreService.setState('app.rap.portInIndicator', val);
  }

  /**
   * Gets the Port In Indicator
   * @returns boolean
   */
  // @TODO Function can be removed after segregation has been completed
  public getPortInIndicator(): boolean {
    return this.stateStoreService.getState('app.rap.portInIndicator');
  }

  /**
   * Gets the Reserved Msisdn of Rap
   */
  // @TODO Function can be removed after segregation has been completed
  public clearRAPSession(): void {
    if (this.stateStoreService.getState('app.rap')) {
      this.stateStoreService.clearState('app.rap');
    }
  }

  /**
   * Gets the Reserved Msisdn of Rap
   * @returns string
   */

  // public setPortInData(portInData: PortInData): void {
  //   this.stateStoreService.setState('app.rap.portInObject', portInData);
  // }

  /**
   * Gets the Reserved Msisdn of Rap
   * @returns string
   */

  // public getPortInData(): PortInData {
  //   return this.stateStoreService.getState('app.rap.portInObject');
  // }

  /**
   * Gets the Reserved Msisdn of Rap
   * @returns string
   */
  // @TODO Function can be removed after segregation has been completed
  public clearPortInData(): void {
    this.stateStoreService.clearState('app.rap.portInObject');
  }

  /**
   * Function to set the Submit Order Response Messages
   * @param messages
   * @returns void
   */
  public setSubmitOrderResponseMessages(messages: SubmitOrderResponseMessages[]): void {
    this.stateStoreService.setState('orderResponseMessages', messages);
  }

  /**
   * Function to get the Submit Order Response Messages
   * @returns SubmitOrderResponseMessages
   */
  public getSubmitOrderResponseMessages(): SubmitOrderResponseMessages[] {
    return this.stateStoreService.getState(`orderResponseMessages`);
  }

  /**
   * Function to clear messages array in submit order
   * @returns void
   */
  public clearSubmitOrderMessage(): void {
    this.stateStoreService.clearState('orderResponseMessages');
  }
  /**
   * Function to set the epin redemption flag
   * @param isSelected
   * @returns void
   */
  public setEpinRedemption(isSelected: boolean): void {
    this.stateStoreService.setState('isSelected', isSelected);
  }

  /**
   * Function to get the epin redemption flag
   * @returns boolean
   */
  public getEpinRedemption(): boolean {
    return this.stateStoreService.getState(`isSelected`);
  }

  /**
   * Function to clear the epin redemption flag
   * @returns void
   */
  public clearEpinRedemption(): void {
    this.stateStoreService.clearState('isSelected');
  }

  /**
   * Function to set field validation constansts
   * @param FieldValidationConstant
   * @returns void
   */
  // @TODO Function can be removed after segregation has been completed
  public setFieldValidationConst(FieldValidationConstant: ObjectInterface): void {
    this.stateStoreService.setState('rap.FieldValidationConstant', FieldValidationConstant);
  }

  /**
   * Function to get the field validation constansts
   * @returns ObjectInterface
   */
  // @TODO Function can be removed after segregation has been completed
  public getFieldValidationConst(): ObjectInterface {
    return this.stateStoreService.getState(`rap.FieldValidationConstant`);
  }

  /**
   * Function to set epay Response
   * @param EpayResponse
   * @returns void
   */
  // @TODO Function can be removed after segregation has been completed
  public setEpayResponse(epay: EpayResponse): void {
    this.stateStoreService.setState('rap.epay', epay);
  }

  /**
   * Function to clear messages array in submit order
   * @returns void
   */
  // @TODO Function can be removed after segregation has been completed
  public clearEpayResponse(): void {
    this.stateStoreService.clearState('rap.epay');
  }

  /**
   * Function to get epay Response
   * @returns EpayResponse
   */
  // @TODO Function can be removed after segregation has been completed
  public getEpayResponse(): EpayResponse {
    return this.stateStoreService.getState('rap.epay');
  }

  /**
   * Function to set Network cash drawer barcode
   */
  public setNetworkBarcode(networkBarcode: string): void {
    this.stateStoreService.setState('networkBarcode', networkBarcode);
  }

  /**
   * Function to get Network cash drawer barcode
   */
  public getNetworkBarcode(): string {
    return this.stateStoreService.getState('networkBarcode');
  }

  /**
   * Function to get Network cash drawer barcode
   */
  public clearNetworkBarcode(): void {
    this.stateStoreService.clearState('networkBarcode');
  }

  /**
   * Function to set the previousCustomerContext- CustomerID
   * @param actionCode
   * @returns void
   */
  public setLatestSubmitOrderActionCode(actionCode: string): void {
    this.stateStoreService.setState('submitOrder.actionCode', actionCode);
  }

  /**
   * Function to get previous customer context - customerID
   * @param void
   * @returns actionCode
   */
  public getLatestSubmitOrderActionCode(): string {
    return this.stateStoreService.getState('submitOrder.actionCode');
  }

  /**
   * Function to clear action code after submit order
   * @returns void
   */
  public removeLatestSubmitOrderActionCode(): void {
    this.stateStoreService.clearState('submitOrder.actionCode');
  }
  /*
   * Function to set is HardStop flag val
   */
  public setIsHardStop(isHardStop: boolean): void {
    this.stateStoreService.setState('isHardStop', isHardStop);
  }

  /**
   * Gets the is Hard Stop val
   * @returns boolean
   */

  public getIsHardStop(): boolean {
    return this.stateStoreService.getState('isHardStop');
  }

  public setRPXMigrationModalStatus(status: boolean): void {
    this.stateStoreService.setState('rpxModalStatus', status);
  }

  public getRPXMigrationModalStatus(): boolean {
    return this.stateStoreService.getState('rpxModalStatus');
  }

  /**
   * method to set line and device tiles
   * @param lineAndDeviceTiles LineAndDeviceTile
   * @returns void
   */
  // public setLineAndDeviceTiles(lineAndDeviceTiles: LineAndDeviceTile[]): void {
  //   this.stateStoreService.setState(this.getCustomerContextKey() + '.lineAndDeviceTiles', lineAndDeviceTiles);
  // }

  /**
   * method to get line and device tiles
   * @returns LineAndDeviceTile[]
   */
  // public getLineAndDeviceTiles(): LineAndDeviceTile[] {
  //   return this.stateStoreService.getState(this.getCustomerContextKey() + '.lineAndDeviceTiles') || [];
  // }

  /**
   * method to set value as true when clear cta is clicked to clear cart
   * @param clearCart boolean
   * @returns void
   */
  public setClearCartStatus(clearCart: boolean): void {
    this.stateStoreService.setState('clearCart', clearCart);
  }

  /**
   * method to get value if clear cta is clicked to clear cart
   * @returns boolean
   */
  public getClearCartStatus(): boolean {
    return this.stateStoreService.getState('clearCart');
  }

  public clearUsageBucketModel(): void {
    this.stateStoreService.clearState('cachedUsageBucketModel');
  }

  public setUsageBucketModel(status: UsageBucketModel): void {
    this.stateStoreService.setState('cachedUsageBucketModel', status);
  }

  public getUsageBucketModel(): UsageBucketModel {
    return this.stateStoreService.getState('cachedUsageBucketModel');
  }
  /**
   * Function to set manager user id for Override
   */
  public setManagerUserID(userId: string): void {
    this.stateStoreService.setState('userId', userId);
  }

  /**
   * Function to get manager user id for Override
   */
  public getManagerUserID(): string {
    return this.stateStoreService.getState('userId');
  }

  /**
   * Function to clear manager user id for Override
   */
  public clearManagerUserID(): void {
    this.stateStoreService.clearState('userId');
  }

  /**
   * Function to set product details
   * @param hardGoodsData Product[]
   * @returns void
   */
  public setItemDetails(hardGoodsData: Product[]): void {
    this.stateStoreService.setState('app.product.hardGoodsData', hardGoodsData);
  }
  /**
   * Function to get product details
   * @returns Product[]
   */
  public getItemDetails(): Product[] {
    return this.stateStoreService.getState('app.product.hardGoodsData');
  }

  /**
   * Function to clear product details
   * @returns void
   */
  public clearItemDetails(): void {
    this.stateStoreService.clearState('app.product.hardGoodsData');
  }

  /**
   * Function to set Instore flag
   * @returns void
   */
  public setInstoreFlow(isInstore: boolean = false): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + '.instoreFlow', isInstore);
  }
  /**
   * Function to get Instore flag
   * @returns boolean
   */
  public getInstoreFlow(): boolean {
    return this.stateStoreService.getState(this.getCustomerContextKey() + '.instoreFlow') || false;
  }

  /**
   * Function to clear Instore Flag
   * @returns void
   */
  public clearInstoreFlow(): void {
    this.stateStoreService.clearState(this.getCustomerContextKey() + '.instoreFlow');
  }

  /**
   * method to get BYOS tile id
   * @returns string
   */
  public getBYOSTile(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + '.tempLineNumber') || '';
  }

  /**
   * method to set BYOS tile id
   * @param tempLineNumber string value of line tile
   * @returns void
   */
  public setBYOSTile(tempLineNumber: string): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + '.tempLineNumber',
      tempLineNumber
    );
  }

  /**
   * method to set existing lines value
   * @param existingLines number
   * @returns void
   */
  public setExistingLinesValue(existingLines: number): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + '.existingLines', existingLines);
  }

  /**
   * method to get existing lines value
   * @returns number
   */
  public getExistingLinesValue(): number {
    return this.stateStoreService.getState(this.getCustomerContextKey() + '.existingLines') || 0;
  }

  /**
   * Function to set Instore flag
   * @returns void
   */
  public setUpgradeDevice(isUpgrade: boolean = false): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + '.isUpgrade', isUpgrade);
  }

  /**
   * Function to get Instore flag
   * @returns boolean
   */
  public getUpgradeDevice(): boolean {
    return this.stateStoreService.getState(this.getCustomerContextKey() + '.isUpgrade') || false;
  }
  /**
   * Function to set Primary Msisdn
   * @returns void
   */
  public setAuthPrimaryMsisdn(primaryMsisdn: string = ''): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + '.authPrimaryMsisdn',
      primaryMsisdn
    );
  }

  /**
   * Function to get Primary Msisdn
   * @returns string
   */
  public getAuthPrimaryMsisdn(): string {
    return (
      this.stateStoreService.getState(this.getCustomerContextKey() + '.authPrimaryMsisdn') || ''
    );
  }

  /**
   * method to set records validation
   * @param validatedRecords boolean
   */
  public setRecordsAsValid(validatedRecords: boolean = false): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + '.validatedRecords',
      validatedRecords
    );
  }

  /**
   * method to get records validation
   */
  public getRecordsAsValid(): boolean {
    return this.stateStoreService.getState(this.getCustomerContextKey() + '.validatedRecords');
  }

  public setHazmatEligibleItems(items: string[]): void {
    this.stateStoreService.setState('hazmatEligibleItems', items);
  }

  public getHazmatEligibleItems(): string[] {
    return this.stateStoreService.getState('hazmatEligibleItems');
  }

  public setHazmatLabelFlag(label: string): void {
    this.stateStoreService.setState('hazmatLabel', label);
  }
  /**
   * Function to get Hazmat flag
   * @returns boolean
   */
  public getHazmatLabelFlag(): boolean {
    if (this.stateStoreService.getState('hazmatLabel')) {
      return this.stateStoreService.getState('hazmatLabel').toLowerCase() === 'yes';
    } else {
      return false;
    }
  }

  public setShippingLabelFlag(isEligible: boolean): void {
    this.stateStoreService.setState('isShippingEligible', isEligible);
  }

  public getShippingLabelFlag(): boolean {
    return this.stateStoreService.getState('isShippingEligible');
  }

  public getCartEditFlag(): boolean {
    return this.stateStoreService.getState('isCartEdit');
  }

  public setCartEditFlag(isCartEdit: boolean): void {
    this.stateStoreService.setState('isCartEdit', isCartEdit);
  }

  public setZipCodeModalFlag(isZipCodeModalClose: boolean): void {
    this.stateStoreService.setState('isZipCodeModalClose', isZipCodeModalClose);
  }

  public getZipCodeModalFlag(): boolean {
    return this.stateStoreService.getState('isZipCodeModalClose');
  }
  /**
   * Used to get cart checkout status flag
   * @returns
   */
  public getCartCheckedOutStatusFlag(): boolean {
    const cartId = this.getCartId();
    return this.stateStoreService.getState(
      this.getCustomerContextKey() + `.cart.${cartId}.cartCheckedOutStatus`
    );
  }

  /**
   * Used to set cart checkout status flag
   * @param cartCheckedOutStatus
   */
  public setCartCheckedOutStatusFlag(cartCheckedOutStatus: boolean): void {
    const cartId = this.getCartId();
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.${cartId}.cartCheckedOutStatus`,
      cartCheckedOutStatus
    );
  }

  /**
   * method to reset retail cart falgs and objects
   */
  public clearCartSession(): void {
    this.deleteCart();
    //  this.setLineAndDeviceTiles([]);
    this.setClearCartStatus(false);
    this.setRecordsAsValid(false);
    this.setAddFan(false);
    this.clearShipFulfillment();
    this.setReservedMsisdn(null);
  }

  public setDefaultFilterCache(filters: Filter[]): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.defaultFilters`, filters);
  }

  public getDefaultFilterCache(): Filter[] {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.defaultFilters`);
  }

  public setCustomerOneTimePinValidationStatus(isVerified: boolean): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + '.isOTPVerified', isVerified);
  }

  public getCustomerOneTimePinValidationStatus(): boolean {
    return this.stateStoreService.getState(this.getCustomerContextKey() + '.isOTPVerified');
  }

  public setManagerOverridePerformedStatus(managerOverridePerformed: boolean): void {
    this.stateStoreService.setState('managerOverridePerformed', managerOverridePerformed);
  }

  public getManagerOverridePerformedStatus(): boolean {
    return this.stateStoreService.getState('managerOverridePerformed');
  }

  public setCustomerVerificationType(customerVerificationType: string): void {
    this.stateStoreService.setState('customerVerificationType', customerVerificationType);
  }

  public getCustomerVerificationType(): string {
    return this.stateStoreService.getState('customerVerificationType');
  }

  public setCustomerOneTimePinValidationObject(
    details: CustomerVerifiedOneTimePin,
    accountID: string
  ): void {
    this.stateStoreService.setState('otpObject.' + accountID, details);
  }

  public getCustomerOneTimePinValidationObject(accountID: string): CustomerVerifiedOneTimePin {
    return this.stateStoreService.getState('otpObject')
      ? this.stateStoreService.getState('otpObject.' + accountID)
      : {};
  }

  public setIsManagerOverrideAllowed(isManagerOverrideEnabled: boolean): void {
    this.stateStoreService.setState('isManagerOverrideAllowed', isManagerOverrideEnabled);
  }

  public isManagerOverrideAllowed(): boolean {
    return this.stateStoreService.getState('isManagerOverrideAllowed');
  }

  public setIsAccountActivationAfterOTPCutoff(isAccountActivationAfterOTPCutoff: boolean): void {
    this.stateStoreService.setState(
      'isAccountActivationAfterOTPCutoff',
      isAccountActivationAfterOTPCutoff
    );
  }

  public isAccountActivationAfterOTPCutoff(): boolean {
    return this.stateStoreService.getState('isAccountActivationAfterOTPCutoff');
  }

  public setImeiCompatibilityCheck(imeiCompatibility: ImeiCheckInterface): void {
    this.stateStoreService.setState(
      this.getCustomerContextKey() + '.imeiCompatibility',
      imeiCompatibility
    );
  }

  public getImeiCompatibilityCheck(): ImeiCheckInterface {
    return this.stateStoreService.getState(this.getCustomerContextKey() + '.imeiCompatibility');
  }

  public setSimValidation(simValidation: ValidateSimResponse): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + '.simValidation', simValidation);
  }

  public getSimValidation(): SimValidation {
    return this.stateStoreService.getState(this.getCustomerContextKey() + '.simValidation');
  }

  public setPdlSubtypeConstants(subTypeConstants: object): void {
    this.stateStoreService.setState('subTypeConstants', subTypeConstants);
  }

  public getPdlSubtypeConstants(): object {
    return this.stateStoreService.getState('subTypeConstants');
  }

  public setPdlSubtypeOverrideConstants(subTypeOverrideConstants: object): void {
    this.stateStoreService.setState('subTypeOverrideConstants', subTypeOverrideConstants);
  }

  public getPdlSubtypeOverrideConstants(): object {
    return this.stateStoreService.getState('subTypeOverrideConstants');
  }

  public setPdlSubtypeForCart(subType: PdlSubtypeCartMapping[]): void {
    this.stateStoreService.setState('subType', subType);
    try {
      localStorage.setItem('PDLSubTypes', JSON.stringify(subType));
    } catch (e) {
      console.error(e);
    }
  }

  public getPdlSubtypeForCart(): PdlSubtypeCartMapping[] {
    const subType = this.stateStoreService.getState('subType');
    if (subType) {
      return subType;
    } else {
      try {
        const data = JSON.parse(localStorage.getItem('PDLSubTypes'));
        if (data) {
          this.setPdlSubtypeForCart(data);
        }
        return data;
      } catch (e) {
        console.error(e);
      }
    }
  }

  public setSEOModifiers(seoModifiers: string): void {
    this.stateStoreService.setState('seoModifiers', seoModifiers);
  }

  public getSEOModifiers(): string {
    return this.stateStoreService.getState('seoModifiers') || '{}';
  }

  public setPageInfoSubCategoryConstantInPDP(subCategoryConstant: object): void {
    this.stateStoreService.setState('subCategoryConstant', subCategoryConstant);
  }

  public getPageInfoSubCategoryConstantInPDP(): object {
    return this.stateStoreService.getState('subCategoryConstant');
  }

  public setPdlCartPaymentConstants(cartPaymentConstants: object): void {
    this.stateStoreService.setState('pdlCartPaymentConstant', cartPaymentConstants);
  }

  public getPdlCartPaymentConstant(method: string): PdlCartPaymentMappingObject {
    const constant = this.stateStoreService.getState('pdlCartPaymentConstant');
    return constant && constant[method];
  }

  public setPdlTypeConstants(typeConstants: object): void {
    this.stateStoreService.setState('typeConstants', typeConstants);
  }

  public getPdlTypeConstants(): object {
    return this.stateStoreService.getState('typeConstants');
  }

  public setExistingOtpEmail(isExistingOtpEmail: boolean): void {
    this.stateStoreService.setState('isExistingOtpEmail', isExistingOtpEmail);
  }

  public getExistingOtpEmail(): boolean {
    return this.stateStoreService.getState('isExistingOtpEmail');
  }

  public setEmailOtpVerified(isEmailOtpValidated: boolean): void {
    this.stateStoreService.setState('isEmailOtpValidated', isEmailOtpValidated);
  }

  public getEmailOtpVerified(): boolean {
    return this.stateStoreService.getState('isEmailOtpValidated');
  }
  /**
   * Function to set waived off shipping price
   * @param {shippingFee}
   */
  public setUpdatedShippingPrice(shippingFee: string): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.shippingFee`, shippingFee);
  }

  /**
   * Function to get waived off shipping price
   * @returns {shippingPrice}
   */
  public getUpdatedShippingPrice(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.shippingFee`);
  }

  public clearEmailVerificationCache(): void {
    this.setExistingOtpEmail(false);
    this.setEmailOtpVerified(false);
    const custConfigData = this.getCustomerConfigData();
    if (custConfigData && custConfigData.email) {
      custConfigData.email = '';
      this.setCustomerConfigData(custConfigData);
    }
  }

  private clearSessionPass(): void {
    if (this.getSessionPassSelectedItem()) {
      this.clearSessionPassSelectedItem();
    }
  }

  public setDurationForSelectedConnectPlan(duration: string): void {
    this.stateStoreService.setState('duration', duration);
  }

  public getDurationForSelectedConnectPlan(): string {
    return this.stateStoreService.getState('duration');
  }

  public setServiceZeroDollarFlag(isZeroDollarService: boolean): void {
    this.stateStoreService.setState('isZeroDollarService', isZeroDollarService);
  }

  public getServiceZeroDollarFlag(): boolean {
    return this.stateStoreService.getState('isZeroDollarService');
  }

  public clearServiceZeroDollarFlag(): void {
    this.stateStoreService.clearState('isZeroDollarService');
  }
  /**
   * Function to set waived off shipping price
   * @param {shippingFee}
   */
  public setSelectedShippingPrice(shippingPrice: string): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.shippingPrice`, shippingPrice);
  }

  /**
   * Function to set waived off shipping price
   * @param {shippingFee}
   */
  public getSelectedShippingPrice(): string {
    return this.stateStoreService.getState(this.getCustomerContextKey() + `.shippingPrice`);
  }
  /**
   * Function to set isCartReview screen
   * @param {isCartReview}
   */
  public setCartReviewPage(isCartReview: boolean): void {
    this.stateStoreService.setState('isCartReview', isCartReview);
  }

  /**
   * Function to get isCartReview screen
   * @param {isCartReview}
   */
  public getCartReviewPage(): boolean {
    return this.stateStoreService.getState('isCartReview');
  }

  /**
   * Function to set new plans page enable/disable state
   * @param {isAddNewSelectionToCartCtaClicked}
   */
  public setNewPlansPage(isAddNewSelectionToCartCtaClicked: boolean): void {
    this.stateStoreService.setState(
      'isAddNewSelectionToCartCtaClicked',
      isAddNewSelectionToCartCtaClicked
    );
  }

  /**
   * Function to get new plans page enable/disable state
   */
  public getNewPlansPage(): boolean {
    return this.stateStoreService.getState('isAddNewSelectionToCartCtaClicked');
  }
  /**
   * Method to get Active plan id for the current line
   * @returns string
   */
  public getCoreOfferFamilyName(): string {
    const currentLine = this.objectService.get(
      this.getCustomerAccountLineBillingDetail(this.getLineId()),
      'lineDetails'
    );
    if (currentLine) {
      return this.objectService.get(currentLine, 'plan.coreOfferFamilyName');
    } else {
      return '';
    }
  }
  /**
   * Function to set reviewContent
   * @param {reviewContent}
   */
  public setCartReviewContent(reviewContent: ConfigurableCartAEMData): void {
    this.stateStoreService.setState('cartReviewContent', reviewContent);
  }

  /**
   * Function to get reviewContent
   * @param {reviewContent}
   */
  public getCartReviewContent(): ConfigurableCartAEMData {
    return this.stateStoreService.getState('cartReviewContent');
  }

  /**
   * Set AutoPay Flag.
   * @param {autopayFlagValue}
   */
  public setAutoPayFlag(autopayFlagValue: boolean): void {
    this.stateStoreService.setState('enableAutopay', autopayFlagValue);
  }

  /**
   * Get AutoPay Flag.
   */
  public getAutoPayFlag(): boolean {
    return this.stateStoreService.getState('enableAutopay');
  }

  /**
   * Set Quantum Metrics Cookie Value
   * @param cookieValue
   */

  public setQMCookie(cookieValue: string): void {
    this.stateStoreService.setState('QMCookie', cookieValue);
  }

  /**
   * Get Quantum Metrics Cookie Value
   * @returns string
   */
  public getQMCookie(): string {
    return this.stateStoreService.getState('QMCookie');
  }

  /**
   * Set Cart Count Cookie Name.
   * @param {cartCookieName}
   */
  public setCartCountCookieName(cartCookieName: string): void {
    this.stateStoreService.setState('cartItemsCount', cartCookieName);
  }

  /**
   * Get Cart Count Cookie Name.
   */
  public getCartCountCookieName(): string {
    return this.stateStoreService.getState('cartItemsCount');
  }

  /**
   * Set ESIM toggle boolean
   * @param esimToggle
   */
  public setEsimToggleEnabled(esimToggle: boolean): void {
    this.stateStoreService.setState('esimToggleEnabled', esimToggle);
  }
  /**
   * Get ESIM toggle boolean
   * @param esimToggle
   */
  public getEsimToggleEnabled(): boolean {
    return this.stateStoreService.getState('esimToggleEnabled');
  }
  /**
   * Set DCC Price toggle boolean
   * @param dccPriceToggle
   */
  public setDccPriceToggleEnabled(dccPriceToggle: boolean): void {
    this.stateStoreService.setState('dccPriceToggleEnabled', dccPriceToggle);
  }
  /**
   * Get DCC Price toggle boolean
   */
  public getDccPriceToggleEnabled(): boolean {
    return this.stateStoreService.getState('dccPriceToggleEnabled');
  }
  /**
   * Set Brand
   * @param brand
   */
  public setBrand(brand: string): void {
    this.stateStoreService.setState('brand', brand);
  }
  /**
   * Get Brand
   * @returns
   */
  public getBrand(): string {
    return this.stateStoreService.getState('brand');
  }
  /**
   *
   * @param imeiValue
   * Set IMEI Value for BYOD flow
   */
  public setImeiValue(imeiValue: string): void {
    this.stateStoreService.setState('imeiValue', imeiValue);
  }
  /**
   *
   * @param imeiKey
   * @returns
   */
  public getImeiValue(imeiKey: string): string {
    return this.stateStoreService.getState(imeiKey);
  }

  public setAuthBillingAddress(data: Address): void {
    this.stateStoreService.setState(this.getCustomerContextKey() + `.billingAuthAddresses`, data);
  }

  public getAuthBillingAddress(): Address {
    return (
      this.stateStoreService.getState(this.getCustomerContextKey() + `.billingAuthAddresses`) || {}
    );
  }
  public setReservedMsisdns(reservedMsisdns: { number: string; msisdnReserved: boolean }[]): void {
    const cartId = this.getCartId();
    this.stateStoreService.setState(
      this.getCustomerContextKey() + `.cart.${cartId}.reservedMsisdns`,
      reservedMsisdns
    );
  }

  public getReservedMsisdns(): { number: string; msisdnReserved: boolean }[] {
    const cartId = this.getCartId();
    return (
      this.stateStoreService.getState(
        this.getCustomerContextKey() + `.cart.${cartId}.reservedMsisdns`
      ) || []
    );
  }

  public setMultiLineScenario(isMultiLine: boolean): void {
    this.stateStoreService.setState('isMultiLine', isMultiLine);
  }

  public getMultiLineScenario(): boolean {
    return this.stateStoreService.getState('isMultiLine');
  }

  public setAutoPayDiscountEligiblePlansInCart(autoPayDiscountEligiblePlanInCart: AutoPayDiscountEligiblePlanInCart): void {
    this.stateStoreService.setState(`autoPayCartPlansEligibility`, autoPayDiscountEligiblePlanInCart);
  }

  public getAutoPayDiscountEligiblePlansInCart(): AutoPayDiscountEligiblePlanInCart {
    return this.stateStoreService.getState(`autoPayCartPlansEligibility`);
  }

}
